import React, { useEffect, useState, useMemo, useRef } from 'react';
import ClientPicker, { ClientOpt } from '../Peak/ClientPicker';
import CollectionCell from '../CollectionCell';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useFocusRow } from '../MyTasks';
import { useBadgeOnly, BadgeFilterBar } from '../BadgeFilter';
import TaxPaidEmailComposer from '../TaxPaidEmailComposer';
import Fab from '../Fab';
import { tabColor, rowTint, menuPill, darken, TAB_GROUPS } from '../../tabPalette';
import { api } from '../../utils/api';
import { useStore } from '../../store';
import { BankTxn, BankMonthly } from '../../types';
import Pagination, { getPageSize, PAGE_SIZES } from '../Pagination';
import MatchPanel from '../MatchPanel';
import DocNoLink from '../DocNoLink';
import ReceiptEmailComposer from '../Peak/ReceiptEmailComposer';
import SlipCell from './SlipCell';
import ReceiptEmailThread from '../Peak/ReceiptEmailThread';
import PropertyTaxEmailThread from '../Tax/PropertyTaxEmailThread';
import PropertyTaxEmailComposer from '../Tax/PropertyTaxEmailComposer';
import { displayClientName, displayCompanyName } from '../../utils/displayName';

const BKK = 'Asia/Bangkok';
const fmtDate = (ymd: string) => { const p = (ymd || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : ymd; };

type Period = 'today' | 'yesterday' | 'week' | 'month' | 'lastMonth' | 'year' | 'custom';
// Compute a {from,to} YYYY-MM-DD range for the chosen period, anchored to TODAY in Bangkok.
// Date maths runs in UTC (Date.UTC + toISOString) so the browser's own timezone never shifts the day.
const rangeFor = (period: Period, cFrom: string, cTo: string): { from: string; to: string } => {
  if (period === 'custom') return { from: cFrom, to: cTo };
  const [y, m, d] = new Date().toLocaleDateString('en-CA', { timeZone: BKK }).split('-').map(Number);
  const base = new Date(Date.UTC(y, m - 1, d));
  const iso = (dt: Date) => dt.toISOString().slice(0, 10);
  const shift = (dt: Date, days: number) => { const x = new Date(dt); x.setUTCDate(x.getUTCDate() + days); return x; };
  if (period === 'today') return { from: iso(base), to: iso(base) };
  if (period === 'yesterday') { const yd = shift(base, -1); return { from: iso(yd), to: iso(yd) }; }
  if (period === 'week') { const dow = base.getUTCDay(); const mon = shift(base, dow === 0 ? -6 : 1 - dow); return { from: iso(mon), to: iso(shift(mon, 6)) }; }
  if (period === 'lastMonth') return { from: iso(new Date(Date.UTC(y, m - 2, 1))), to: iso(new Date(Date.UTC(y, m - 1, 0))) };
  if (period === 'year') return { from: iso(new Date(Date.UTC(y, 0, 1))), to: iso(new Date(Date.UTC(y, 11, 31))) };
  // month
  return { from: iso(new Date(Date.UTC(y, m - 1, 1))), to: iso(new Date(Date.UTC(y, m, 0))) };
};
const fmtDT = (u: number | null) => u ? new Date(u * 1000).toLocaleString('en-GB', { timeZone: BKK, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) : '';
const money = (n: any) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Compact stamp for the Collection "Paid" line: "dd/mm HH:MM" (no year) — keeps the narrow column tidy.
const fmtPaid = (u: number) => { if (!u) return ''; const d = new Date(u * 1000); const p = (n: number) => String(n).padStart(2, '0'); return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`; };
const monthLabel = (ym: string) => { const [y, m] = ym.split('-'); return new Date(Number(y), Number(m) - 1, 1).toLocaleString('en-US', { month: 'long' }) + ' ' + y; };

const input: React.CSSProperties = { width: '100%', padding: '9px 12px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none' };
const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 };
const btn = (bg: string, color = '#fff'): React.CSSProperties => ({ padding: '8px 14px', borderRadius: 'var(--radius-md)', border: 'none', background: bg, color, fontSize: 13, fontWeight: 600, cursor: 'pointer' });

// Fixed column layout so the header and every row line up cleanly.
const COLDEFS: { key: string; w: number; align: 'left' | 'right' | 'center' }[] = [
  // Ticking rows is how several deposits become one tax payment (owner 2026-09-08).
  { key: '_sel', w: 34, align: 'center' },
  { key: 'bs_date', w: 92, align: 'left' },
  { key: 'bs_description', w: 170, align: 'left' },
  { key: 'bs_debit', w: 112, align: 'right' },
  { key: 'bs_credit', w: 112, align: 'right' },
  // Sized for the amount (Calem, report 2133118d); the tax breakdown and Paid ticks pop down from the
  // round toggle beside it (<CollectionCell popDown>).
  { key: 'bs_collection', w: 128, align: 'right' },
  { key: 'bs_balance', w: 122, align: 'right' },
  { key: 'bs_channel', w: 76, align: 'left' },
  { key: 'bs_client', w: 130, align: 'left' },
  { key: 'bs_company', w: 150, align: 'left' },
  // Up from 132 by the 52px Comments gave up (Calem, report d7c20856).
  { key: 'bs_quote_receipt', w: 184, align: 'left' },
  // A quarter narrower than its old 168 (Calem, report 33249b9a). The cell's 4px 6px padding and the
  // slip zone's own 4px padding + 2px border leave ~101px: thumb 44 + gap 7 + add button 44 still sit
  // side by side, and the +N badge hangs 6px into that 7px gap.
  { key: 'bs_slip', w: 126, align: 'left' },
  // A fifth narrower than its old 260 (Calem, report d7c20856). The 52px went to Quotation/Invoice, so
  // the table's total width, and every other column, stays as it was.
  { key: 'bs_comments', w: 208, align: 'left' },
  // 'Receipt (matched)' (Calem, report c0d493d0) — its own key, as bs_match still heads Petty Cash's
  // column, which leads with the quotation/invoice number.
  { key: 'bs_receipt_matched', w: 210, align: 'left' },
  { key: 'bs_type', w: 84, align: 'center' },
  { key: 'bs_recorded', w: 188, align: 'left' },
  { key: '_actions', w: 78, align: 'right' },
];
// Hidden for everyone unless management opens them with the toolbar chevron (Calem, report 33249b9a).
const EXTRA_COLS = new Set(['bs_balance', 'bs_channel', 'bs_recorded']);
// Management = admin, Calem or Nick — mirrors server/src/routes/files.ts isManagement, as FileLibrary does.
const MANAGEMENT_EMAILS = new Set(['calem@slf-legal.co', 'nick@slf-accounting.co']);

// Account-type label. 'client' = the SCB Dedicated Client Account (money held FOR clients) — it
// must never be mislabelled as one of the firm's own accounts, so this is a 3-way map, not a
// "not personal → corporate" fallback.
export function acctTypeKey(type: string): 'cs_corporate' | 'cs_personal' | 'cs_client' {
  return type === 'personal' ? 'cs_personal' : type === 'client' ? 'cs_client' : 'cs_corporate';
}
const ACCT_TONE: Record<string, { bg: string; fg: string }> = {
  corporate: { bg: '#E4F0FB', fg: '#2B6CB0' },
  personal: { bg: '#FBF0E4', fg: '#B4894A' },
  client: { bg: '#E3EFE8', fg: '#276749' },
};
function TypeBadge({ type }: { type: string }) {
  const { t } = useTranslation();
  if (type !== 'corporate' && type !== 'personal' && type !== 'client') return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
  const tone = ACCT_TONE[type];
  return <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: tone.bg, color: tone.fg, whiteSpace: 'nowrap' }}>{t(acctTypeKey(type))}</span>;
}

// ── Add / edit modal ────────────────────────────────────────────────────────
function TxnForm({ scope, txn, onClose, onSaved }: { scope: string; txn: BankTxn | null; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const isNew = !txn;
  const [f, setF] = useState({
    accountType: txn?.account_type || (scope === 'client' ? 'client' : 'corporate'), date: txn?.txn_date || new Date().toLocaleDateString('en-CA', { timeZone: BKK }),
    description: txn?.description || '', debit: String(txn?.debit ?? 0), credit: String(txn?.credit ?? 0),
    balance: txn?.balance == null ? '' : String(txn.balance), channel: txn?.channel || '',
    clientName: txn?.client_name || '', counterCompany: txn?.counter_company || '', quoteReceipt: txn?.quote_receipt || '', comments: txn?.comments || '',
  });
  const set = (k: string, v: any) => setF(p => ({ ...p, [k]: v }));
  const [err, setErr] = useState(''); const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true); setErr('');
    const data = { ...f, debit: Number(f.debit) || 0, credit: Number(f.credit) || 0, balance: f.balance === '' ? null : Number(f.balance) };
    try {
      if (isNew) await api.bankCreate(scope, data); else await api.bankUpdate(scope, txn!.id, data);
      onSaved(); onClose();
    } catch (e: any) { setErr(e.message); setSaving(false); }
  };
  const field = (label: string, k: keyof typeof f, type = 'text') => (
    <div><label style={lbl}>{label}</label><input type={type} value={(f as any)[k]} onChange={e => set(k as string, e.target.value)} style={input} /></div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: 520, maxHeight: '90vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', padding: 24, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: 'var(--text-primary)' }}>{isNew ? t('bs_add') : t('edit')}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={lbl}>{t('cs_acct_type')}</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {scope === 'client' ? (
                <span style={{ padding: '8px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: '#E3EFE8', color: '#276749', fontSize: 13, fontWeight: 700 }}>{t('cs_client')}</span>
              ) : (['corporate', 'personal'] as const).map(v => (
                <button key={v} onClick={() => set('accountType', v)} style={{ flex: 1, padding: '8px', borderRadius: 'var(--radius-md)', border: '1px solid', borderColor: f.accountType === v ? 'var(--accent)' : 'var(--border-strong)', background: f.accountType === v ? 'var(--accent)' : 'transparent', color: f.accountType === v ? '#fff' : 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{t(v === 'corporate' ? 'cs_corporate' : 'cs_personal')}</button>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>{field(t('bs_date'), 'date', 'date')}</div>
            <div style={{ flex: 1 }}>{field(t('bs_channel'), 'channel')}</div>
          </div>
          {field(t('bs_description'), 'description')}
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>{field(t('bs_debit'), 'debit', 'number')}</div>
            <div style={{ flex: 1 }}>{field(t('bs_credit'), 'credit', 'number')}</div>
            <div style={{ flex: 1 }}>{field(t('bs_balance'), 'balance', 'number')}</div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>{field(t('bs_client'), 'clientName')}</div>
            <div style={{ flex: 1 }}>{field(t('bs_company'), 'counterCompany')}</div>
          </div>
          {field(t('bs_quote_receipt'), 'quoteReceipt')}
          {field(t('bs_comments'), 'comments')}
          {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)' }}>{err}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={onClose} style={btn('var(--c-gray-100)', 'var(--text-secondary)')}>{t('cancel')}</button>
            <button onClick={save} disabled={saving} style={btn('var(--accent)')}>{saving ? t('login_wait') : t('save')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// KBank's own Details text, which the statement import copies into Comments: a direction, the other
// bank's code, the masked account, then a name the bank cut short with '++' — "From BAY X6615 MISS
// SOPHIE BO KWA++", "To SCB X9322 PLOYPAIRIN ++", "Paid for Ref X7529 Krungsri Credit Cards".
// Group 1 is the name that is left. Reference-only lines ("Trade Ref no. …", "Ref Code …") don't fit.
const BANK_DETAIL = /^(?:From|To|Paid for Ref)\s+(?:[A-Za-z]{2,12}\s+)?X\d{3,5}\s+(.*?)\+*\s*$/;

// Comments with SYSTEM-written lines (matched-doc remark, moved names, no-match reason — tracked
// in bank_transactions.sys_lines) rendered GREEN so they stand out from hand-typed notes.
// A bank Details line shows only a client name (Calem, report 2133118d): the one the AI picked for
// the row — its suggestion, or the client once matched — and with no pick (unmatched, no-match, or a
// debit, which the AI never matches) the name left once the direction, bank, X-account and '++' are
// off. That name is tidied as the Client column is (Calem, report 101b5c69): titles, middle names and a
// company's legal form off. Display only: the stored line still feeds the matcher's note and the
// search, and shows whole on hover and in the preview's Notes.
function CommentsCell({ row }: { row: BankTxn }) {
  let sys = new Set<string>();
  try { const a = JSON.parse((row as any).sys_lines || '[]'); if (Array.isArray(a)) sys = new Set(a.map((x: any) => String(x).trim()).filter(Boolean)); } catch { /* plain render */ }
  const st = row.match_state || '';
  const picked = (st === 'suggested' ? row.match?.clientName || ''
    : st === 'matched' || st === 'partial' ? row.match?.clientName || row.counter_company || row.client_name || '' : '').trim();
  const lines = String(row.comments || '').split('\n');
  return (
    <>{lines.map((ln, i) => {
      const tl = ln.trim();
      const bank = tl && !sys.has(tl) ? BANK_DETAIL.exec(tl) : null;
      const name = bank ? displayClientName(picked || (bank[1] || '').trim(), row.client_first, row.client_last) : '';
      return (
        <React.Fragment key={i}>
          {i > 0 && '\n'}
          {sys.has(tl) && tl
            ? <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{ln}</span>
            : name ? <span title={tl}>{name}</span>
            : ln}
        </React.Fragment>
      );
    })}</>
  );
}

// ── Quick preview modal ─────────────────────────────────────────────────────
// Opens on a row click: a read-only detail card for the transaction, with two quick actions —
// match the (incoming) payment to a Quotation or Invoice, and edit the free-text Notes — without
// having to open the full edit form.
function TxnPreview({ scope, txn, onClose, onSaved, onEditFull, onMatch, onOpenReceipt, onUnmatch, onRematch }: {
  scope: string; txn: BankTxn; onClose: () => void; onSaved: () => void;
  onEditFull: () => void; onMatch: () => void; onOpenReceipt: () => void; onUnmatch: () => void; onRematch: () => void;
}) {
  const { t } = useTranslation();
  const [notes, setNotes] = useState(txn.comments || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const dirty = (notes || '') !== (txn.comments || '');
  const isCredit = !!txn.credit;
  const st = txn.match_state || 'none';

  const saveNotes = async () => {
    setSaving(true); setErr('');
    try { await api.bankSetNotes(scope, txn.id, notes); onSaved(); onClose(); }
    catch (e: any) { setErr(e.message); setSaving(false); }
  };

  const sectionLbl: React.CSSProperties = { fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-tertiary)', marginBottom: 8 };
  const hint: React.CSSProperties = { fontSize: 11.5, color: 'var(--text-tertiary)' };
  const Row = (label: string, node: React.ReactNode) => (
    <div style={{ display: 'flex', gap: 12, padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{ width: 132, flexShrink: 0, fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{label}</div>
      <div style={{ flex: 1, fontSize: 13, color: 'var(--text-primary)', wordBreak: 'break-word' }}>{node}</div>
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: 560, maxHeight: '92vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', borderRadius: 14, boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
          <i className="ti ti-eye" style={{ color: 'var(--accent)' }} />
          <div style={{ fontSize: 15, fontWeight: 800 }}>{t('bs_preview_title')}</div>
          <div style={{ flex: 1 }} />
          <button onClick={onEditFull} title={t('edit')} style={{ ...btn('var(--c-gray-100)', 'var(--text-secondary)'), padding: '6px 10px' }}><i className="ti ti-pencil" style={{ marginRight: 5 }} />{t('edit')}</button>
          <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: 18 }}><i className="ti ti-x" /></button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px', display: 'flex', flexDirection: 'column' }}>
          {Row(t('bs_date'), fmtDate(txn.txn_date))}
          {Row(t('cs_acct_type'), <TypeBadge type={txn.account_type} />)}
          {!!txn.channel && Row(t('bs_channel'), txn.channel)}
          {Row(t('bs_description'), txn.description || '—')}
          {Row(t('bs_debit'), txn.debit ? <span style={{ color: '#E24B4A', fontWeight: 700 }}>฿{money(txn.debit)}</span> : '–')}
          {Row(t('bs_credit'), txn.credit ? <span style={{ color: '#2B6CB0', fontWeight: 700 }}>฿{money(txn.credit)}</span> : '–')}
          {Row(t('bs_balance'), txn.balance == null ? '—' : '฿' + money(txn.balance))}
          {/* Tidied as the list shows them (report 101b5c69); Edit opens the names as stored. */}
          {!!txn.client_name && Row(t('bs_client'), displayClientName(txn.client_name, txn.client_first, txn.client_last))}
          {!!txn.counter_company && Row(t('bs_company'), displayCompanyName(txn.counter_company))}
          {!!txn.quote_receipt && Row(t('bs_quote_receipt'), txn.quote_receipt)}
          {Row(t('bs_recorded'), <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{txn.created_by_name || '—'} · {fmtDT(txn.created_at)}{txn.edited === 1 ? ' · ✎ ' + fmtDT(txn.updated_at) : ''}</span>)}

          {/* Match — Quotation / Invoice only */}
          <div style={{ marginTop: 16 }}>
            <div style={sectionLbl}>{t('bs_match_section')}</div>
            {!isCredit ? (
              <div style={hint}>{t('bs_match_credit_only')}</div>
            ) : st === 'matched' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 13, color: '#15803D', fontWeight: 700 }}><i className={txn.match?.docKind === 'property_tax' ? 'ti ti-receipt-tax' : 'ti ti-circle-check'} /> {t('bs_matched')} · <DocNoLink no={txn.match?.docNo || t('bs_receipt')} id={txn.match?.docId} kind={txn.match?.docKind} />{txn.match?.clientName ? ' · ' + displayClientName(txn.match.clientName, txn.client_first, txn.client_last) : ''}</div>
                {txn.match?.docKind === 'property_tax' && <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}><i className="ti ti-info-circle" /> {t('bs_pt_no_receipt')}</div>}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {txn.match?.docKind !== 'property_tax' && <button onClick={onOpenReceipt} style={btn('var(--bg-active)', 'var(--accent)')}><i className="ti ti-file-type-pdf" style={{ marginRight: 6 }} />{t('bs_open_receipt')}</button>}
                  <button onClick={onRematch} style={btn('var(--c-gray-100)', 'var(--text-secondary)')}><i className="ti ti-refresh" style={{ marginRight: 6 }} />{t('bs_rematch')}</button>
                  <button onClick={onUnmatch} style={btn('transparent', '#E24B4A')}><i className="ti ti-arrow-back-up" style={{ marginRight: 6 }} />{t('bs_unmatch')}</button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {st === 'suggested' && txn.match && <div style={{ fontSize: 12.5, color: '#1D4ED8' }}><i className="ti ti-bulb" /> <DocNoLink no={t('bs_suggest_for', { client: displayClientName(txn.match.clientName, txn.client_first, txn.client_last) || '?', no: txn.match.docNo || '' })} id={txn.match.docId} kind={txn.match.docKind} /></div>}
                <button onClick={onMatch} style={{ ...btn('var(--accent)'), alignSelf: 'flex-start' }}><i className="ti ti-link" style={{ marginRight: 6 }} />{t('bs_match_to_doc')}</button>
                <div style={hint}>{t('bs_match_doc_hint')}</div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div style={{ marginTop: 16 }}>
            <div style={sectionLbl}>{t('bs_notes')}</div>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder={t('bs_notes_ph')} style={{ ...input, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
            {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 8, marginTop: 8 }}>{err}</div>}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, padding: '12px 18px', borderTop: '1px solid var(--border)' }}>
          <button onClick={onClose} style={btn('var(--c-gray-100)', 'var(--text-secondary)')}>{t('cancel')}</button>
          <div style={{ flex: 1 }} />
          <button onClick={saveNotes} disabled={saving || !dirty} style={{ ...btn('var(--accent)'), opacity: (saving || !dirty) ? 0.6 : 1, cursor: (saving || !dirty) ? 'default' : 'pointer' }}><i className="ti ti-device-floppy" style={{ marginRight: 6 }} />{saving ? t('login_wait') : t('bs_save_notes')}</button>
        </div>
      </div>
    </div>
  );
}

// ── Reusable Bank Statement view ────────────────────────────────────────────
export function BankStatementView({ scope }: { scope: 'accounting' | 'legal' | 'client' }) {
  const { t } = useTranslation();
  const me = useStore(s => s.me);
  const [rows, setRows] = useState<BankTxn[]>([]);
  const [monthly, setMonthly] = useState<Record<string, BankMonthly>>({});
  const [accounts, setAccounts] = useState<any[]>([]);
  const [lastScan, setLastScan] = useState<number>(0);
  // Whether this user may confirm a client's payment slip (Calem / Nick). Server-decided; the
  // button is only hidden here, the POST is what actually refuses (owner 2026-09-18).
  const [canSlipConfirm, setCanSlipConfirm] = useState(false);
  const [search, setSearch] = useState('');   // toolbar quick filter — text and amounts, all three tabs
  // Month whose balances the pinned bar shows — follows the scroll (topmost visible row's month).
  const [viewYm, setViewYm] = useState('');
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const onTableScroll = () => {
    const cont = tableScrollRef.current; if (!cont) return;
    const top = cont.getBoundingClientRect().top + 36;   // just under the sticky column header
    for (const tr of Array.from(cont.querySelectorAll('tr[data-ym]'))) {
      if ((tr as HTMLElement).getBoundingClientRect().bottom >= top) {
        const ym = (tr as HTMLElement).dataset.ym || '';
        setViewYm(v => (v === ym ? v : ym));
        return;
      }
    }
  };
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [account, setAccount] = useState<'all' | 'corporate' | 'personal'>('all');
  // Date filter. 'week' only stands in until the opening bubble is chosen (see the opening-period
  // effect beside the list load); periodReady flips once it has been.
  const [period, setPeriod] = useState<Period>('week');
  const [periodReady, setPeriodReady] = useState(false);
  const todayBkk = new Date().toLocaleDateString('en-CA', { timeZone: BKK });
  const [customFrom, setCustomFrom] = useState<string>(todayBkk);
  const [customTo, setCustomTo] = useState<string>(todayBkk);
  const [downloading, setDownloading] = useState(false);
  // Client Account only: whose money to show. '' = everyone, '__none' = not yet assigned.
  const [clientId, setClientId] = useState('');
  const [clientOpts, setClientOpts] = useState<{ tagged: any[]; untagged: number; all: any[] }>({ tagged: [], untagged: 0, all: [] });
  const [assignBusy, setAssignBusy] = useState<string | null>(null);
  const [taxEmailFor, setTaxEmailFor] = useState<string | null>(null);   // VAT/WHT payment-received notice
  // Searchable client picker (fixed overlay so the table's overflow can't clip it). 430+ clients, so
  // NOTHING is listed until the operator types — a full dropdown would be unusable.
  const [clientPick, setClientPick] = useState<{ id: string; x: number; y: number } | null>(null);
  const [clientQuery, setClientQuery] = useState('');
  const [loading, setLoading] = useState(true);
  useFocusRow(!loading);   // deep link from the tasks bell: scroll to & flash that transaction
  // Badge drill-down (?badge=1): show ONLY the unmatched incoming payments the red badge counts.
  const { badgeOnly, clearBadge } = useBadgeOnly();
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState<BankTxn | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [importing, setImporting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<BankTxn | null>(null);
  const [matchKind, setMatchKind] = useState<'vat' | 'wht' | 'pnd51' | undefined>(undefined);   // VAT / WHT / PND.51 entry from the Match dropdown
  const [menuFor, setMenuFor] = useState<{ id: string; x: number; y: number } | null>(null);   // open Match/No-match dropdown
  const [customFor, setCustomFor] = useState<BankTxn | null>(null);                             // custom no-match reason modal
  // Notary Public asks WHO the notarisation was done for. Staff were already typing that by hand
  // through the free-text option ("Notary : Ursula"), so the name is captured as two fields instead —
  // same result, one consistent format that can actually be searched later.
  const [notaryFor, setNotaryFor] = useState<BankTxn | null>(null);
  const [notaryFirst, setNotaryFirst] = useState('');
  const [notaryLast, setNotaryLast] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [recId, setRecId] = useState<string | null>(null);        // row whose fresh AI recommendation is loading
  const [noMatchBusy, setNoMatchBusy] = useState<string | null>(null);
  const [matching, setMatching] = useState(false);
  const [pdfBusyId, setPdfBusyId] = useState<string | null>(null);
  const [preview, setPreview] = useState<BankTxn | null>(null);
  // Tax pot (owner 2026-09-08): tick the deposits that funded one tax, and the payment that sent it
  // on, and attach them to ONE obligation. The rows themselves are never merged — the bank said
  // five deposits and the running balance is built line by line.
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [potModal, setPotModal] = useState(false);
  const [potOpen, setPotOpen] = useState<string>('');      // pot id being viewed
  const [emailFor, setEmailFor] = useState<{ receiptId: string; name: string } | null>(null);     // receipt email editor
  const [threadFor, setThreadFor] = useState<{ receiptId: string; docNo: string; name: string } | null>(null); // ✉ thread viewer
  const [ptThreadFor, setPtThreadFor] = useState<{ clientId: string; year: number; name: string } | null>(null);   // property-tax ✉ thread
  const [ptEmailFor, setPtEmailFor] = useState<{ clientId: string; year: number } | null>(null);                    // property-tax payment email
  type QrSlot = 'full' | 'invoice';
  const [qrZoom, setQrZoom] = useState<{ acc: any; src: string; slot: QrSlot } | null>(null);
  const [qrBusy, setQrBusy] = useState<string | null>(null);   // `${accountId}:${slot}` while uploading
  // The account / payment-QR panel collapses (default) so the transactions table gets the screen room.
  const [acctOpen, setAcctOpen] = useState<boolean>(() => { try { return localStorage.getItem('bs_acctOpen') === '1'; } catch { return false; } });
  const toggleAcct = () => setAcctOpen(o => { const n = !o; try { localStorage.setItem('bs_acctOpen', n ? '1' : '0'); } catch { /* ignore */ } return n; });
  // Balance, Channel and Recorded / Audit (EXTRA_COLS) open from a toolbar chevron remembered the same
  // way. Only management gets the chevron, and a choice another login left on this browser never opens
  // them for anyone else (Calem, report 33249b9a).
  const isMgmt = me?.role === 'admin' || MANAGEMENT_EMAILS.has(String(me?.email || '').toLowerCase());
  const [extraCols, setExtraCols] = useState<boolean>(() => { try { return localStorage.getItem('bs_extraCols') === '1'; } catch { return false; } });
  const toggleExtraCols = () => setExtraCols(o => { const n = !o; try { localStorage.setItem('bs_extraCols', n ? '1' : '0'); } catch { /* ignore */ } return n; });
  const showExtra = isMgmt && extraCols;
  const cols = showExtra ? COLDEFS : COLDEFS.filter(c => !EXTRA_COLS.has(c.key));
  const tableW = cols.reduce((s, c) => s + c.w, 0);

  // Read an image file and downscale it (canvas) to a small PNG data URL — keeps the QR crisp and scannable
  // while small enough to store on the account and ship in the list payload.
  const fileToQrDataUrl = (file: File, max = 900): Promise<string> => new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
        const c = document.createElement('canvas'); c.width = w; c.height = h;
        const ctx = c.getContext('2d'); if (!ctx) return reject(new Error('canvas'));
        ctx.drawImage(img, 0, 0, w, h);
        resolve(c.toDataURL('image/png'));
      };
      img.onerror = () => reject(new Error('image')); img.src = fr.result as string;
    };
    fr.onerror = () => reject(new Error('read')); fr.readAsDataURL(file);
  });
  const uploadQr = async (acc: any, file: File, slot: QrSlot) => {
    if (!file.type.startsWith('image/')) { alert(t('bs_qr_invalid')); return; }
    setQrBusy(`${acc.id}:${slot}`);
    try { const url = await fileToQrDataUrl(file); await api.bankSetAccountQr(scope, acc.id, url, slot); await load(page, account); setQrZoom(z => z && z.slot === slot ? { ...z, src: url } : z); }
    catch (e: any) { alert(e.message); } finally { setQrBusy(null); }
  };
  const removeQr = async (acc: any, slot: QrSlot) => {
    setQrBusy(`${acc.id}:${slot}`);
    try { await api.bankSetAccountQr(scope, acc.id, '', slot); setQrZoom(null); await load(page, account); }
    catch (e: any) { alert(e.message); } finally { setQrBusy(null); }
  };
  // One QR slot (thumbnail + upload), reused for the full and the simplified/invoice codes — identical behaviour.
  const qrSlot = (a: any, slot: QrSlot, value: string, label: string) => (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.02em', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: 4 }}>{label}</div>
      {value ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src={value} alt="QR" onClick={() => setQrZoom({ acc: a, src: value, slot })}
            style={{ width: 46, height: 46, objectFit: 'contain', background: '#fff', border: '1px solid var(--border)', borderRadius: 6, cursor: 'zoom-in' }} />
          <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t('bs_qr_hint')}</span>
        </div>
      ) : (
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 700, color: 'var(--accent)', cursor: qrBusy === `${a.id}:${slot}` ? 'default' : 'pointer' }}>
          <i className="ti ti-qrcode" style={{ fontSize: 14 }} />{qrBusy === `${a.id}:${slot}` ? t('bs_qr_uploading') : t('bs_qr_add')}
          <input type="file" accept="image/*" style={{ display: 'none' }} disabled={qrBusy === `${a.id}:${slot}`}
            onChange={e => { const f = e.target.files?.[0]; if (f) uploadQr(a, f, slot); e.currentTarget.value = ''; }} />
        </label>
      )}
    </div>
  );

  // Effective date range for the active filter — drives both the list and the PDF export.
  const { from: rangeFrom, to: rangeTo } = rangeFor(period, customFrom, customTo);

  const [pageSize, setPageSize] = useState(getPageSize());
  const load = async (p = page, acc = account) => {
    setLoading(true); setErr('');
    try {
      // Badge mode ignores the date window and sub-account filters — the badge count has none,
      // and the list must show exactly the rows the number promised.
      const res = badgeOnly
        ? await api.bankList(scope, { page: p, unmatched: true, limit: pageSize })
        : await api.bankList(scope, { account: acc === 'all' ? undefined : acc, page: p, from: rangeFrom || undefined, to: rangeTo || undefined, clientId: clientId || undefined, q: search.trim() || undefined, limit: pageSize });
      setRows(res.rows); setTotal(res.total); setPage(res.page);
      const map: Record<string, BankMonthly> = {};
      (res.monthly || []).forEach((m: BankMonthly) => { map[m.ym] = m; });
      setMonthly(map);
      setAccounts(res.accounts || []);
      setLastScan(res.lastScan || 0);
      setCanSlipConfirm(!!res.canSlipConfirm);
    } catch (e: any) { setErr(e.message); } finally { setLoading(false); }
  };

  // One-time auto-match of pending inbound credits (server enforces once-only).
  // Loops a few bounded batches, then refreshes the list.
  // Triggered ONLY by the two import paths and the toolbar button. It used to hang off the
  // filter effect below, so opening the module — and then every change of account tab, firm scope,
  // either date bound, client filter, badge drill-down or page size — re-fired up to 15 batches of
  // AI calls (owner 2026-09-18). The in-flight guard stops overlapping runs stacking.
  const runAutomatch = async () => {
    if (matching) return;
    setMatching(true);
    try {
      for (let i = 0; i < 15; i++) {
        const r: any = await api.bankAutomatchPending(scope);
        if (r.processed > 0) await load(page, account);
        if (!r.remaining || r.processed === 0) break;
      }
    } catch { /* non-blocking */ } finally { setMatching(false); }
  };

  // Refresh = go to the bank mailbox NOW and pull any new statements (same job the cron runs), then
  // reload the list + re-run auto-match. The scan is global (shared mailbox), so a Legal/Accounting
  // click fetches for both. Reports what it found; tells the user if a scan was already running.
  const scanNow = async () => {
    if (scanning) return;
    setScanning(true); setErr('');
    try {
      const r: any = await api.bankScan(scope);
      await load(1, account);
      runAutomatch();
      if (r.skipped) alert(t('bs_fetch_busy'));
      else if ((r.added || 0) > 0 || (r.imported || 0) > 0) alert(t('bs_fetch_done', { added: r.added || 0, statements: r.imported || 0 }));
      else alert(t('bs_fetch_none'));
    } catch (e: any) { setErr(e.message); } finally { setScanning(false); }
  };

  // Opening period (Calem, Bugify #498a3cef): the list always opened on This week, so a quiet week
  // opened on an empty table. Open instead on the first of This week → This month → Last month that
  // holds a line (struck-out deleted lines don't count). This week covers today; Last month is the
  // answer whether it has lines or not, so it is never asked. Chosen once per mount, the first time
  // the dated list is needed — badge mode has no date window, so there it waits for the badge to be
  // cleared — and a bubble clicked in the meantime wins: its click sets periodReady, cancelling this.
  useEffect(() => {
    if (periodReady || badgeOnly) return;
    let alive = true;
    setLoading(true);
    (async () => {
      let pick: Period = 'lastMonth';
      for (const p of ['week', 'month'] as const) {
        const { from, to } = rangeFor(p, '', '');
        try {
          // The smallest page the server accepts — all that is asked is "is there a live line?".
          const res = await api.bankList(scope, { from, to, limit: PAGE_SIZES[0] });
          if (!alive) return;
          if ((res.rows || []).some((r: BankTxn) => r.deleted !== 1)) { pick = p; break; }
        } catch {
          if (!alive) return;
          pick = 'week'; break;   // couldn't ask — keep the old default; the list load reports the error
        }
      }
      setPeriod(pick); setPeriodReady(true);
    })();
    return () => { alive = false; };
  }, [periodReady, badgeOnly]); // eslint-disable-line
  // The list's own loads wait for that choice (badge mode needs none), so an empty week never flashes first.
  useEffect(() => { if (!badgeOnly && !periodReady) return; (async () => { await load(1, account); })(); }, [account, scope, rangeFrom, rangeTo, clientId, badgeOnly, pageSize, periodReady]); // eslint-disable-line
  // Server-wide search (owner 2026-08-31): the box used to filter only the page already loaded.
  // Debounced refetch — the server ignores the date window / account tab while q is set.
  // Debounce only REAL search input — on mount this effect used to fire a second, 400ms-late
  // duplicate of the initial load, repainting the whole table twice (owner 2026-09-06).
  // Held like the load above: a search typed before the opening period is chosen rides on the load
  // that the choice triggers. (React.StrictMode re-runs this effect on mount in dev; its stray
  // This-week load could otherwise land after the chosen period's and empty the table.)
  const searchInit = useRef(true);
  useEffect(() => { if (searchInit.current) { searchInit.current = false; return; } const t = setTimeout(() => { if (!badgeOnly && !periodReady) return; void load(1, account); }, 400); return () => clearTimeout(t); }, [search]); // eslint-disable-line

  // The slip cells invite dragging files at a dense table — a drop that misses its 126px target
  // must NOT let the browser navigate the whole app to the dropped file. preventDefault here is
  // harmless for the cells themselves (their handlers run first, in the target phase).
  useEffect(() => {
    const guard = (e: DragEvent) => { e.preventDefault(); };
    window.addEventListener('dragover', guard);
    window.addEventListener('drop', guard);
    return () => { window.removeEventListener('dragover', guard); window.removeEventListener('drop', guard); };
  }, []);

  // Client list + balances (refreshed whenever rows change, so a re-tag updates the balances).
  const loadClients = async () => {
    if (scope !== 'client') return;
    try { setClientOpts(await api.bankClients(scope)); } catch { /* non-blocking */ }
  };
  useEffect(() => { void loadClients(); }, [scope, rows]); // eslint-disable-line

  // Tag one line to a client — the balances and (if filtering) the list refresh straight after.
  // Patch ONLY the affected row (no list reload) so the table never unmounts/flashes. Balances
  // refresh on their own via the rows-keyed effect below, which carries no loading state.
  const assignClient = async (txnId: string, cid: string) => {
    setAssignBusy(txnId);
    try {
      const r: any = await api.bankSetClient(scope, txnId, cid);
      setRows(prev => prev
        .map(x => (x.id === txnId ? { ...x, client_id: r.clientId, client_name: r.clientName } : x))
        // While filtering by one client, a row reassigned to someone else drops out of view.
        .filter(x => !clientId || x.client_id === clientId));
    } catch (e: any) { setErr(e.message); } finally { setAssignBusy(null); }
  };

  // Download the current filter's transactions as a PDF. Guarded (spinner + disabled) against double-clicks.
  const download = async () => {
    if (downloading) return;
    setDownloading(true); setErr('');
    try {
      await api.bankExportPdf(scope, { account: account === 'all' ? undefined : account, from: rangeFrom || undefined, to: rangeTo || undefined, clientId: clientId || undefined });
    } catch (e: any) { setErr(e.message); } finally { setDownloading(false); }
  };

  const del = async (txn: BankTxn) => {
    if (!window.confirm(t('bs_confirm_delete'))) return;
    await api.bankDelete(scope, txn.id).catch((e: any) => setErr(e.message));
    load();
  };

  const onImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    setImporting(true); setErr('');
    try {
      const r = await api.bankImportPdf(scope, file);
      let msg = t('bs_import_done', { added: r.added, skipped: r.skipped });
      if (!r.matched) msg += '\n' + t('bs_import_unmatched', { acct: r.accountNumber || '?' });
      alert(msg);
      await load(1, account);
      runAutomatch();
    } catch (err: any) { setErr(err.message); } finally { setImporting(false); }
  };

  // Header / cell styles for the fixed-layout table. The header on the page's white, --bg-surface, as the Jobs lists'
  // header is (Calem, report ddf1ba0e, 3rd opening: "Change every page background to white"). It was the light grey,
  // --c-gray-50 (Calem, report c0d8d666: "Revert to background light gray color for all menus that look similar to
  // this"), and the near-white before that.
  // Each transaction is a rounded card, built the way the Jobs lists build theirs (cardLine there) — Calem, report
  // a0ef7195: "Change the entire banking menu (row detail) to bubbles". The list was a grid, a thin grey line down and
  // across every cell and a heavier one under the header; none of those is left. The top menu's Bank box made a card:
  // its wash is the fill (plainBg, below), its line the card's edge — TAB_GROUPS.bank.border, 1px as on the box — with
  // the Jobs and Tracking cards' 10px corners. The cells draw it (cell): across the top and bottom of every cell, down
  // the checkbox cell's left and the actions cell's right, rounded at the card's four corners (edgeL / edgeR). Cards
  // stand CARD_GAP apart (cardGap) and CARD_INSET in from the list's edges — the table's padding — on the page's white,
  // --bg-surface, set on the scroll box so it runs on under the last card; it was the list's grey, --c-gray-50 (Calem,
  // report ddf1ba0e, 3rd opening — see the page). Every column keeps its width (see the table). The edge is a shade
  // darker than the box's line since that opening ("darken up the bubble border on each page by one shade"): 0.51 →
  // 0.63, a quarter of the way on to the solid #1D9E75 (see darken), so on white it goes #8CCEB9 → #71C2A8. The box, the
  // account and period pills and the search box keep the box's line.
  const cardLine = `1px solid ${darken(TAB_GROUPS.bank.border, 1)}`;
  const CARD_R = 10;
  const CARD_GAP = 4;
  const CARD_INSET = 17;
  // 11 → 12px, and the thin grey line under the headings again, the Peak and Jobs lists' own: 1px of --border along the
  // bottom of every heading, carried out to the list's edges (thEdge) — Calem, report 66c9d174, 2nd opening: "Do the
  // same for all other menus in the SLF system. Make sure to include the horizontal gray line for any pages that
  // scroll". The line adds a pixel to the header, so the list starts a pixel lower; no column changes width.
  // Then Active Jobs' headings, as every staff list's: black, --text-primary, at the regular weight, where they were the
  // dark grey --text-secondary in bold (700), 10px above and below where it was 9, on one 16px line, over the darker
  // --border-strong line (Calem, report 4bd4ae4e, 2nd opening: "Make all menus in the SLF system look like this: the
  // same height, text color, no bold, horizontal line colors etc."). The 12px stays; the band is a pixel or two taller,
  // 37px as every list's is, so the list starts that much lower. No column changes width.
  const thBase: React.CSSProperties = { fontSize: 12, fontWeight: 400, color: 'var(--text-primary)', padding: '10px 10px', lineHeight: '16px', borderBottom: '1px solid var(--border-strong)', position: 'sticky', top: 0, background: 'var(--bg-surface)', zIndex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
  // The header's white carried on over the table's CARD_INSET padding to the list's edges, leftwards from the first
  // header cell and rightwards from the last, as on the Jobs lists (thEdge there): the cards come in, the header
  // doesn't. Inside a header cell, so it stays on the header as the list scrolls under it. It carries the header's
  // line out to the edges too, reaching a pixel below the cell's inside (bottom −1) so the two lines meet (Calem,
  // report 66c9d174 — see thBase), the darker --border-strong now (report 4bd4ae4e).
  const thEdge: React.CSSProperties = { position: 'absolute', top: 0, bottom: -1, width: CARD_INSET, background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-strong)' };
  const tdBase: React.CSSProperties = { fontSize: 12.5, color: 'var(--text-primary)', padding: '8px 10px', verticalAlign: 'top', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
  const cell = (align: 'left' | 'right' | 'center', extra: React.CSSProperties = {}): React.CSSProperties => ({ ...tdBase, borderTop: cardLine, borderBottom: cardLine, textAlign: align, ...extra });
  const edgeL: React.CSSProperties = { borderLeft: cardLine, borderTopLeftRadius: CARD_R, borderBottomLeftRadius: CARD_R };
  const edgeR: React.CSSProperties = { borderRight: cardLine, borderTopRightRadius: CARD_R, borderBottomRightRadius: CARD_R };
  // The page above each card, and a strip more under the last one. See-through by a colour set on the row, not by having
  // none: global.css's zebra shades every other row of a list with a header, so a gap on an even row would come out darker.
  const cardGap = (key: string) => (
    <tr key={key} aria-hidden="true" style={{ background: 'transparent' }}><td colSpan={cols.length} style={{ padding: 0, height: CARD_GAP, border: 'none' }} /></tr>
  );
  // Long text columns (names / doc no / comments) wrap within their fixed width instead of truncating.
  const WRAP: React.CSSProperties = { whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word', overflow: 'visible' };

  // The account pills and the period pills below wear the top menu's Bank box, the one this page's button sits in, as
  // its "+" does (Calem, Bugify #667b55cb — see menuPill). The selected one's solid green #1D9E75 is the app's green, so
  // it looks as it did; the rest take the box's pale wash, green words and its line, where they were page grey with a
  // grey outline.
  const menuKey = { accounting: 'bank_acc', legal: 'bank_legal', client: 'bank_client' }[scope];
  const acctTab = (key: 'all' | 'corporate' | 'personal', label: string) => (
    <button onClick={() => setAccount(key)} style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600, borderRadius: 'var(--radius-md)', border: '1px solid', ...menuPill(menuKey, account === key), cursor: 'pointer' }}>{label}</button>
  );

  // A click also settles the opening period — made while that is still being chosen, it wins.
  const periodChip = (key: Period, label: string) => (
    <button onClick={() => { setPeriod(key); setPeriodReady(true); }} style={{ padding: '5px 12px', fontSize: 12, fontWeight: 600, borderRadius: 999, border: '1px solid', ...menuPill(menuKey, period === key), cursor: 'pointer' }}>{label}</button>
  );

  // The search box is a period pill, unselected (Calem, Bugify #cba01676, 5th opening: "Bring the search bubble down to
  // the next level and make it the same styling as the rounded colored bubble, as per the rest on the same line"): round
  // ends, their 1px line, 5px above and below and 12px semibold, so it stands as tall as they do, in the Bank box's
  // colours from menuPill — its pale green wash, its line and green words, the placeholder's too (mf-ph-inherit) — with
  // the magnifier in the green, 12px in where the pills' words start, and the words 30px in, clear of it; the treatment
  // Jobs' and Tracking's search boxes had. It stands at the right-hand end of the period row, just before Download PDF,
  // and on the top row, where it was, only while the badge strip hides the period row. Its width and the "Searching ALL
  // records" note, now just left of the box, are as they were. It was an 8px-cornered box with the faint grey outline on
  // the app's beige, --bg-app, its grey magnifier 10px in and its words 28px, and the note shared its wrapper, so the
  // magnifier sat on the note's first letters.
  const searchBox = (
    <>
      {search.trim() && <span style={{ fontSize: 11.5, fontWeight: 700, color: '#B45309', whiteSpace: 'nowrap' }}>{t('bs_search_all', { n: total })}</span>}
      <div style={{ position: 'relative' }}>
        <i className="ti ti-search" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: tabColor(menuKey) }} aria-hidden />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('pc_search')} className="mf-ph-inherit" style={{ width: 200, padding: '5px 12px 5px 30px', fontSize: 12, fontWeight: 600, borderRadius: 999, border: '1px solid', outline: 'none', ...menuPill(menuKey, false) }} />
      </div>
    </>
  );

  // Open the receipt email editor (from office, to the client's matched email, TPL-022 subject/body).
  const emailMatched = (r: BankTxn) => { if (r.receipt_id) setEmailFor({ receiptId: r.receipt_id, name: r.match?.clientName || r.client_name || '' }); };
  // ── Remittance date per TAX (owner 2026-08-22) ──────────────────────────────────────────────
  // A transfer covers several taxes and each leaves on a different day, so a single "Paid" tick
  // could never be right. Ticking stamps TODAY as a starting point; the date stays editable because
  // the tick almost always happens after the money actually went out.
  const todayISO = () => { const d = new Date(Date.now() + 7 * 3600 * 1000); return d.toISOString().slice(0, 10); };

  // Collection cell (grouping + per-tax Paid) is the shared <CollectionCell> — one rendering for
  // Bank Statement and Petty Cash, so the two can never drift (owner 2026-08-24).
  const setTaxPaid = async (r: BankTxn, taxKey: string, paidOn: string) => {
    const before = rows;
    setRows(rs => rs.map(x => {
      if (x.id !== r.id) return x;
      const marks = (x.collectionPaid || []).filter(m => m.taxKey !== taxKey);
      return { ...x, collectionPaid: paidOn ? [...marks, { taxKey, paidOn, byName: '' }] : marks };
    }));
    // Roll back to the truth, then rethrow: <CollectionCell> shows an in-place NOT SAVED +
    // retry, which an operator cannot dismiss by accident the way an alert() could be.
    try { await api.bankSetCollectionPaid(scope, r.id, taxKey, paidOn); }
    catch (e: any) { setRows(before); throw e; }
  };
  // The pot's "paid to the authority" tick, from the statement row. One call updates every leg of
  // the pot at once, so the whole list reloads rather than patching one row — five rows share the
  // chip and all of them must flip together.
  const [potBusy, setPotBusy] = useState('');
  const bkkTodayISO = () => new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 10);
  const setPotRemitted = async (p: any, on: string) => {
    setPotBusy(p.potId);
    try { await api.bankPotRemitted(scope, p.potId, on); await load(); }
    catch (e: any) { alert(e?.message || 'Failed'); }
    finally { setPotBusy(''); }
  };
  // Take one line back out of its tax payment, straight from the statement. The row returns to
  // exactly the state the pot found it in — so the badge counts it again and the Match ▾ comes
  // back — and both the line and the pot record who did it.
  const detachRow = async (r: BankTxn) => {
    const p: any = (r as any).pot;
    if (!p) return;
    if (!window.confirm(t('bs_pot_detach_row_confirm', { tax: String(p.taxKey).toUpperCase(), client: p.clientName || '?' }))) return;
    try { await api.bankPotDetach(scope, p.potId, r.id); load(); } catch (e: any) { alert(e.message); }
  };
  const undoMatch = async (r: BankTxn) => {
    if (!window.confirm(t('bs_unmatch_confirm'))) return;
    try { await api.bankUnmatch(scope, r.id); load(); } catch (e: any) { alert(e.message); }
  };
  // Re-match a matched line (the earlier match was wrong): void the issued receipt, then re-open the
  // match flow (manual mode) so a different invoice/quotation can be picked.
  const rematch = async (r: BankTxn) => {
    if (!window.confirm(t('bs_rematch_confirm'))) return;
    try { if (r.receipt_id) await api.bankUnmatch(scope, r.id); setSelectedMatch({ ...r, match: null, match_state: 'unmatched', receipt_id: '' }); load(); }
    catch (e: any) { alert(e.message); }
  };
  const openPdf = async (id: string) => { if (pdfBusyId) return; setPdfBusyId(id); try { await api.openReceiptPdf(id); } catch { /* ignore */ } finally { setPdfBusyId(null); } };

  // The per-row Match cell (inbound credit rows only).
  // ── Match / No-match dropdown ──────────────────────────────────────────────
  // Canonical (English) reasons written to Comments so the record is language-stable.
  const NM_FIXED: { label: string; reason: string; icon: string; ask?: 'notary' }[] = [
    { label: t('bs_nm_interest'), reason: 'Interest', icon: 'ti ti-coin' },
    { label: t('bs_nm_annual_fee'), reason: 'Annual Account Fee', icon: 'ti ti-file-invoice' },
    { label: t('bs_nm_wrong_account'), reason: 'Transferred to Wrong Account', icon: 'ti ti-arrows-exchange' },
    { label: t('bs_nm_notary'), reason: 'Notary Public', icon: 'ti ti-stamp', ask: 'notary' },
  ];
  const rowById = (id: string): BankTxn | null => rows.find(x => x.id === id) || null;
  const openMenu = (e: React.MouseEvent, id: string) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const MENU_H = 250;   // flip above the button when it would overflow the viewport bottom (grows with the row count)
    const y = (rect.bottom + 4 + MENU_H > window.innerHeight) ? Math.max(8, rect.top - MENU_H - 4) : rect.bottom + 4;
    setMenuFor({ id, x: Math.max(8, Math.min(rect.left, window.innerWidth - 250)), y });
  };
  // "Match Quotation/Invoice" — pull a FRESH AI recommendation (even after a previous unmatch), then
  // open the match panel pre-filled with it (or in manual mode if nothing was found).
  const openMatch = async (r: BankTxn) => {
    if (recId) return;
    setRecId(r.id);
    try {
      const res: any = await api.bankRecommend(scope, r.id);
      const s = res?.suggestion;
      setSelectedMatch({ ...r, match: (s && s.docId) ? s : null });
    } catch { setSelectedMatch(r); }
    finally { setRecId(null); }
  };
  const doNoMatch = async (r: BankTxn, reason: string) => {
    setNoMatchBusy(r.id);
    try { await api.bankNoMatch(scope, r.id, reason); await load(); }
    catch (e: any) { alert(e.message); }
    finally { setNoMatchBusy(null); }
  };
  const cancelNoMatch = async (r: BankTxn) => {
    setNoMatchBusy(r.id);
    try { await api.bankUnmatch(scope, r.id); await load(); }
    catch (e: any) { alert(e.message); }
    finally { setNoMatchBusy(null); }
  };

  const matchCell = (r: BankTxn): React.ReactNode => {
    if (r.deleted === 1 || !r.credit) return null;
    const st = r.match_state || 'none';
    // Grouped into a tax payment (owner 2026-09-10). This line's match decision is already made, so
    // the column shows WHAT it was decided to be and offers no way to re-decide it here — a Match ▾
    // on a pot line let an operator match tax money to an invoice and orphan the obligation. Both
    // controls route through the pot: Open shows it, Remove detaches (and the pot refuses while it
    // is settled or already paid to the authority).
    if (st === 'pot') {
      const p: any = (r as any).pot;
      if (!p) return <span style={{ color: 'var(--text-tertiary)', fontSize: 11.5 }}>{t('bs_pot_locked')}</span>;
      const paid = !!p.remittedOn;
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start', maxWidth: 210 }}>
          <span title={t('bs_pot_locked_hint')} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 8, background: 'rgba(124,58,237,0.10)', border: '1px solid rgba(124,58,237,0.35)', color: '#6D28D9', fontSize: 11.5, fontWeight: 800, maxWidth: '100%' }}>
            <i className="ti ti-lock" style={{ fontSize: 12, flexShrink: 0 }} aria-hidden />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {String(p.taxKey).toUpperCase()} · {displayClientName(p.clientName) || '?'}
            </span>
          </span>
          <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)', fontWeight: 700 }}>
            {p.period}
            {p.role === 'remittance' ? ` · ${t('bs_pot_role_out')}` : ''}
            {paid ? ` · ${t('bs_pot_paid_on', { date: p.remittedOn })}` : p.status === 'settled' ? ' · ✓' : ''}
          </span>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <button onClick={() => setPotOpen(p.potId)} style={{ border: 'none', background: 'none', padding: 0, color: 'var(--accent)', fontWeight: 700, fontSize: 11.5, cursor: 'pointer' }}>{t('bs_pot_open')}</button>
            <button onClick={() => detachRow(r)} disabled={paid || p.status === 'settled'}
              title={paid ? t('bs_pot_locked_paid') : p.status === 'settled' ? t('bs_pot_locked_settled') : t('bs_pot_detach')}
              style={{ border: 'none', background: 'none', padding: 0, color: (paid || p.status === 'settled') ? 'var(--text-tertiary)' : '#B45309', fontWeight: 700, fontSize: 11.5, cursor: (paid || p.status === 'settled') ? 'not-allowed' : 'pointer' }}>{t('bs_pot_remove')}</button>
          </div>
        </div>
      );
    }
    if (st === 'matched') {
      const iconBtn: React.CSSProperties = { border: 'none', background: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 24, borderRadius: 6 };
      // Property-tax matches issue NO receipt — the tax is collected on the client's behalf. So the
      // receipt-specific controls (open PDF, email receipt, email thread) don't apply; only rematch/unmatch do.
      const pt = r.match?.docKind === 'property_tax';
      // VAT / WHT: no receipt either — but they DO have a payment-received notice (owner 2026-08-28).
      const taxNotice = r.match?.docKind === 'vat' || r.match?.docKind === 'wht' || r.match?.docKind === 'pnd51';
      // The receipt's OWN number, with no tick (Calem, report c0d493d0): the IV-/QO- it settles already
      // heads the Quotation or Invoice column, and repeating it here was crossed out. A row matched
      // before 2026-08-29 has no receiptNo — its docNo is the receipt number. Property tax keeps its
      // icon; the spinner still shows while the PDF opens.
      const icon = pt ? 'ti ti-receipt-tax' : pdfBusyId === r.receipt_id ? 'ti ti-loader-2' : '';
      return (
        // Two lines: receipt number on top, action icons below — so nothing overflows into Account.
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
          <span onClick={() => !pt && r.receipt_id && openPdf(r.receipt_id)} style={{ color: '#15803D', fontWeight: 700, cursor: pt ? 'default' : 'pointer', whiteSpace: 'nowrap' }} title={pt ? t('bs_pt_no_receipt') : t('bs_open_receipt')}>{icon && <><i className={icon} style={pdfBusyId === r.receipt_id ? { display: 'inline-block', animation: 'spin 1s linear infinite' } : undefined} />{' '}</>}<DocNoLink no={(r.match as any)?.receiptNo || r.match?.docNo || t('bs_receipt')} id={r.receipt_id} kind="receipt" /></span>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {/* Email thread + send. Receipts use the receipt thread; property-tax matches use the
                property-tax thread/composer (same tracking, no receipt involved). */}
            {!pt && !taxNotice && <>
            <button onClick={() => r.receipt_id && setThreadFor({ receiptId: r.receipt_id, docNo: r.match?.docNo || '', name: r.match?.clientName || r.client_name || '' })} title={t('bs_email_thread')}
              style={{ ...iconBtn, width: 'auto', padding: '0 6px', gap: 3, color: (r.emailCount || 0) > 0 ? 'var(--accent)' : 'var(--text-tertiary)', fontWeight: 700, fontSize: 12 }}>
              <i className="ti ti-mail" style={{ fontSize: 15 }} />{r.emailCount || 0}
            </button>
            <button onClick={() => emailMatched(r)} title={t('bs_send_email')} style={{ ...iconBtn, color: 'var(--accent)' }}><i className="ti ti-send" style={{ fontSize: 15 }} /></button>
            </>}
            {taxNotice && (
              <button onClick={() => setTaxEmailFor(r.id)} title={t('tp_send_notice')}
                style={{ ...iconBtn, width: 'auto', padding: '0 7px', gap: 4, color: 'var(--accent)', fontWeight: 700, fontSize: 12 }}>
                <i className="ti ti-send" style={{ fontSize: 15 }} />{(r as any).tax_email_count || 0}
              </button>
            )}
            {pt && r.match?.ptClientId && <>
            <button onClick={() => setPtThreadFor({ clientId: r.match!.ptClientId!, year: r.match!.ptYear || new Date().getFullYear(), name: r.match?.clientName || r.client_name || '' })} title={t('bs_email_thread')}
              style={{ ...iconBtn, width: 'auto', padding: '0 6px', gap: 3, color: (r.emailCount || 0) > 0 ? 'var(--accent)' : 'var(--text-tertiary)', fontWeight: 700, fontSize: 12 }}>
              <i className="ti ti-mail" style={{ fontSize: 15 }} />{r.emailCount || 0}
            </button>
            <button onClick={() => setPtEmailFor({ clientId: r.match!.ptClientId!, year: r.match!.ptYear || new Date().getFullYear() })} title={t('bs_send_email')} style={{ ...iconBtn, color: 'var(--accent)' }}><i className="ti ti-send" style={{ fontSize: 15 }} /></button>
            </>}
            <button onClick={() => rematch(r)} title={t('bs_rematch')} style={{ ...iconBtn, color: '#B4894A' }}><i className="ti ti-refresh" style={{ fontSize: 15 }} /></button>
            <button onClick={() => undoMatch(r)} title={t('bs_unmatch')} style={{ ...iconBtn, color: 'var(--text-tertiary)' }}><i className="ti ti-arrow-back-up" style={{ fontSize: 15 }} /></button>
          </div>
        </div>
      );
    }
    // No-match classification (Interest / Annual Account Fee / Wrong Account / custom, or a legacy dismiss).
    if (st === 'ignored') {
      const reason = r.match?.reason || t('bs_no_match_generic');
      const busy = noMatchBusy === r.id;
      return (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%' }}>
          <span title={reason} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 8, background: '#FEF3F2', border: '1px solid #FECDCA', color: '#B42318', fontSize: 11.5, fontWeight: 600, overflow: 'hidden' }}>
            <i className="ti ti-ban" style={{ fontSize: 12, flexShrink: 0 }} /><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{reason}</span>
          </span>
          <button onClick={() => cancelNoMatch(r)} disabled={busy} title={t('bs_cancel_nomatch')} style={{ border: 'none', background: 'none', cursor: busy ? 'default' : 'pointer', color: 'var(--text-tertiary)', padding: 2 }}><i className={busy ? 'ti ti-loader' : 'ti ti-x'} style={{ fontSize: 14 }} /></button>
        </div>
      );
    }
    // Partially allocated: the receipts issued so far, the unallocated remainder, and the Match
    // button to CONTINUE — one transfer settles several documents across several sittings.
    if (st === 'partial') {
      const allocs: any[] = (r as any).allocations || [];
      const iconBtn: React.CSSProperties = { border: 'none', background: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 22, borderRadius: 6 };
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
          {allocs.map(a => (
            <span key={a.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
              <span onClick={() => a.receipt_id && openPdf(a.receipt_id)} style={{ color: '#15803D', fontWeight: 700, cursor: a.receipt_id ? 'pointer' : 'default', fontSize: 12 }} title={a.receipt_id ? t('bs_open_receipt') : undefined}>
                <i className="ti ti-circle-check" /> <DocNoLink no={a.receipt_no || a.doc_no} id={a.receipt_id} kind="receipt" />
              </span>
              {a.receipt_id && <button onClick={() => setEmailFor({ receiptId: a.receipt_id, name: a.client_name || '' })} title={t('bs_send_email')} style={{ ...iconBtn, color: 'var(--accent)' }}><i className="ti ti-send" style={{ fontSize: 13 }} /></button>}
            </span>
          ))}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <button onClick={() => setSelectedMatch(r)} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 8, border: '1px solid #D97706', background: '#FFFBEB', color: '#B45309', cursor: 'pointer', fontSize: 11.5, fontWeight: 700 }}>
              <i className="ti ti-plug-connected" style={{ fontSize: 12 }} /> {t('bs_match_btn')} · ฿{money((r as any).remaining || 0)}
            </button>
            <button onClick={() => undoMatch(r)} title={t('bs_unmatch')} style={{ ...iconBtn, color: 'var(--text-tertiary)' }}><i className="ti ti-arrow-back-up" style={{ fontSize: 14 }} /></button>
          </div>
        </div>
      );
    }
    // Un-matched (none / unmatched / suggested): the Match ▾ dropdown. Suggested rows also show the AI hint.
    const busy = recId === r.id || noMatchBusy === r.id;
    const ddBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', cursor: busy ? 'default' : 'pointer', fontSize: 11.5, fontWeight: 600, flexShrink: 0 };
    if (st === 'suggested') return (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%' }}>
        <button onClick={() => setSelectedMatch(r)} title={t('bs_suggest_for', { client: displayClientName(r.match?.clientName, r.client_first, r.client_last) || '?', no: r.match?.docNo || '' })} style={{ minWidth: 0, textAlign: 'left', display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 8px', borderRadius: 8, border: '1px solid #BFDBFE', background: '#EFF6FF', color: '#1D4ED8', cursor: 'pointer', fontSize: 11.5, fontWeight: 600, overflow: 'hidden' }}>
          <i className="ti ti-bulb" style={{ fontSize: 13, flexShrink: 0 }} /><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayClientName(r.match?.clientName, r.client_first, r.client_last) || '?'}{r.match?.docNo ? <> · <DocNoLink no={r.match.docNo} id={r.match.docId} kind={r.match.docKind} /></> : ''}</span>
        </button>
        <button onClick={e => openMenu(e, r.id)} disabled={busy} title={t('bs_match_btn')} style={{ ...ddBtn, padding: '3px 5px' }}><i className={busy ? 'ti ti-loader' : 'ti ti-chevron-down'} style={{ fontSize: 13 }} /></button>
      </div>
    );
    return (
      <button onClick={e => openMenu(e, r.id)} disabled={busy} style={ddBtn}>
        <i className={busy ? 'ti ti-loader' : 'ti ti-plug-connected'} style={{ fontSize: 12 }} /> {busy ? '…' : t('bs_match_btn')} <i className="ti ti-chevron-down" style={{ fontSize: 12 }} />
      </button>
    );
  };

  // Toolbar quick filter — same behaviour as Petty Cash (owner 2026-08-25): text fields AND amounts,
  // amounts matched raw and display-formatted, commas stripped on both sides. Client-side over the
  // rows the current tab/period loaded.
  const searched = useMemo(() => {
    const needle = search.trim().toLowerCase().replace(/,/g, '');
    if (!needle) return rows;
    // The server already searched the whole book — re-filtering here against a slightly different
    // field list would silently drop real hits. Rows ARE the result set.
    return rows;
    return rows.filter(r => {
      const nums = [r.debit, r.credit, r.balance].filter(v => Number(v)).flatMap(v => [String(v), money(v)]);
      const hay = [r.description, r.client_name, r.counter_company, r.quote_receipt, r.match?.docNo, r.match?.clientName,
        ...((r as any).allocations || []).map((a: any) => a.doc_no), r.comments, r.channel, r.account_type,
        r.created_by_name, fmtDate(r.txn_date), r.txn_date, ...nums].map(v => String(v ?? '')).join(' ').toLowerCase().replace(/,/g, '');
      return hay.includes(needle);
    });
  }, [rows, search]);

  // The monthly summary line lives ONLY in the pinned top bar now (owner 2026-08-25: "这里不用
  // 显示了" — the in-table month header duplicated it). The bar follows the scroll (see tableRef),
  // so whichever month the operator is looking at, its balances are the ones on screen.
  const bodyRows: React.ReactNode[] = [];
  // Every row is the top menu's Bank green, three shades lighter, on all three bank lists (Calem, Bugify #1a85b4b0, 5th
  // opening: "All banking backgrounds should be light green" — see rowTint). The box's #E1F5EE went to #E9F8F2 at one
  // shade, as Quotation, Invoice and Receipt take; to #F0FAF7 at two (Calem 2026-09-28, Bugify #37d916aa, 3rd opening:
  // "Make all banking background green color, one shade lighter"); and is #F8FDFB at three, as Document Storage takes
  // (Calem 2026-09-28, Bugify #33249b9a, 3rd opening, asked again of the #F0FAF7 rows: "make the screen detail
  // background one shade lighter"). Were stripes by account type, blue for corporate and amber for personal,
  // alternating in strength; the Type badge still tells the two apart, and each row is its own card now (report
  // a0ef7195 — see cardLine), where a grey line under each cell separated them. Deleted rows keep their reddish wash
  // and their fade.
  const plainBg = rowTint('bank_acc', 3)!;
  searched.forEach(r => {
    const del0 = r.deleted === 1;
    const rowBg = del0 ? '#FCEBEB55' : plainBg;
    // Client / Company as the list shows them, tooltips included (Calem, report 101b5c69): titles, middle
    // names and a company's legal form off. The stored names stay whole — Edit, search and the PDF use them.
    const clientShown = displayClientName(r.client_name, r.client_first, r.client_last);
    const companyShown = displayCompanyName(r.counter_company);
    // A gap row above each card (see cardGap). The row's colour stays on the row, as on the Jobs cards, where the
    // rounded end cells give it its corners: on the cells it would cover the red ring a tasks-bell link flashes on it.
    bodyRows.push(
      cardGap(`gap-${r.id}`),
      <tr key={r.id} data-row-id={r.id} data-ym={(r.txn_date || '').slice(0, 7)} onClick={() => { if (!del0) setPreview(r); }} title={del0 ? undefined : t('bs_preview_open')} style={{ background: rowBg, opacity: del0 ? 0.6 : 1, cursor: del0 ? 'default' : 'pointer' }}>
        <td style={cell('center', edgeL)} onClick={ev => ev.stopPropagation()}>
          <input type="checkbox" checked={sel.has(r.id)} style={{ cursor: 'pointer' }}
            onChange={e => setSel(prev => { const n = new Set(prev); if (e.target.checked) n.add(r.id); else n.delete(r.id); return n; })} />
        </td>
        {/* Time on its own line, not beside the date: the column is 92px and the whole table is
            already 17 columns wide. It only appears when the statement carried one (owner
            2026-09-19 — same-day transfers of the same amount are otherwise indistinguishable). */}
        <td style={cell('left', { textDecoration: del0 ? 'line-through' : 'none', color: 'var(--text-secondary)' })} title={`${fmtDate(r.txn_date)}${r.txn_time ? ' ' + r.txn_time : ''}`}>
          {fmtDate(r.txn_date)}
          {!!r.txn_time && <div style={{ fontSize: 10.5, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums', lineHeight: 1.3 }}>{r.txn_time}</div>}
        </td>
        <td style={cell('left')} title={r.description}>{r.description}</td>
        <td style={cell('right', { color: r.debit ? '#E24B4A' : 'var(--text-tertiary)', fontWeight: r.debit ? 600 : 400 })}>{r.debit ? '฿' + money(r.debit) : '–'}</td>
        <td style={cell('right', { color: r.credit ? '#2B6CB0' : 'var(--text-tertiary)', fontWeight: r.credit ? 600 : 400 })}>{r.credit ? '฿' + money(r.credit) : '–'}</td>
        {/* Collection (代收代付): pass-through taxes on the matched doc; the round toggle pops down the breakdown (VAT/WHT/SSO…). */}
        <td style={cell('right', { verticalAlign: 'top' })}>
          {/* A funding leg's tax is remitted BY THE POT, which cascades the fact down to this line
              (taxPots.setPotRemitted). Lock that one family here so the mirror cannot be cleared
              on its own and left disagreeing with the pot (owner 2026-09-16). */}
          <CollectionCell row={r} onSetPaid={(taxKey, paidOn) => setTaxPaid(r, taxKey, paidOn)} popDown
            lockedKeys={(r as any).pot && String((r as any).pot.role || 'funding') === 'funding'
              ? [String((r as any).pot.taxKey || '')] : []} />
          {/* Owner 2026-09-08: every line that funded a tax says so — the pot is not a merge, so
              the row keeps its own identity and carries a link to the obligation it paid into. */}
          {(r as any).pot && (() => {
            const p: any = (r as any).pot;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, marginTop: 3 }}>
                <span role="button" title={t('bs_pot_badge_hint', { client: displayClientName(p.clientName) })}
                  onClick={ev => { ev.stopPropagation(); setPotOpen(p.potId); }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 3, maxWidth: '100%', background: 'rgba(124,58,237,0.10)', color: '#7C3AED', border: '1px solid rgba(124,58,237,0.35)', borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 800, cursor: 'pointer' }}>
                  <i className="ti ti-coins" style={{ fontSize: 11 }} aria-hidden />
                  {/* Owner 2026-09-10: "PND51" alone did not say WHOSE. Tax · client · period, so the
                      statement row identifies the obligation without opening anything. One run of text
                      that wraps (report 2133118d): the column is sized for the amount now, and cut off
                      at its width the badge would lose the client and the period. */}
                  <span style={{ whiteSpace: 'normal', overflowWrap: 'anywhere', textAlign: 'left' }}>
                    {String(p.taxKey).toUpperCase()} · {displayClientName(p.clientName) || '?'} · {p.period}
                    {p.role === 'remittance' ? ` · ${t('bs_pot_role_out')}` : ''}
                    {p.status === 'settled' && !p.remittedOn ? ' ✓' : ''}
                  </span>
                </span>
                {/* The simplest possible tick (owner 2026-09-10) — one checkbox writing the pot's
                    "paid to the authority" fact. It is worded REMITTED, not "Paid" (owner
                    2026-09-16): the line above mirrors this very tick, and two controls reading
                    "Paid" with the same date looked like the screen had drawn one thing twice.
                    Not "Paid to RD" — a pot can hold SSO, which goes to the Social Security
                    Office, so the wording stays authority-neutral. The row wraps, so the date drops
                    under the tick in the narrow column. */}
                <div onClick={ev => ev.stopPropagation()} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', gap: 4, fontSize: 10 }}>
                  <label title={t('bs_pot_remitted_tick')}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: potBusy === p.potId ? 'wait' : 'pointer', color: p.remittedOn ? '#15803D' : 'var(--text-tertiary)', fontWeight: p.remittedOn ? 700 : 400 }}>
                    <input type="checkbox" checked={!!p.remittedOn} disabled={potBusy === p.potId} style={{ cursor: 'inherit' }}
                      onChange={e => setPotRemitted(p, e.target.checked ? (p.suggestedOn || bkkTodayISO()) : '')} />
                    {t('bs_pot_remitted')}
                  </label>
                  {potBusy === p.potId && <i className="ti ti-loader" style={{ fontSize: 11, color: 'var(--text-tertiary)' }} aria-hidden />}
                  {p.remittedOn && (
                    <input type="date" value={p.remittedOn} disabled={potBusy === p.potId}
                      onChange={e => e.target.value && setPotRemitted(p, e.target.value)}
                      style={{ fontSize: 10, padding: '0 2px', border: '1px solid var(--border)', borderRadius: 4, background: 'var(--bg-app)', color: '#15803D', fontWeight: 700 }} />
                  )}
                </div>
              </div>
            );
          })()}
        </td>
        {showExtra && <>
          <td style={cell('right', { color: 'var(--text-secondary)' })}>{r.balance == null ? '' : '฿' + money(r.balance)}</td>
          <td style={cell('left', { color: 'var(--text-secondary)' })} title={r.channel}>{r.channel}</td>
        </>}
        {/* Client / Company — matched rows link to the client's detail page (as in Contact). */}
        {scope === 'client' ? (
          // Client Account: the Client cell is an editable picker — every line must say WHOSE money it is.
          <td style={cell('left', WRAP)} onClick={e => e.stopPropagation()}>
            <button
              onClick={e => {
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                const H = 300;
                const y = (rect.bottom + 4 + H > window.innerHeight) ? Math.max(8, rect.top - H - 4) : rect.bottom + 4;
                setClientQuery(''); setClientPick({ id: r.id, x: Math.max(8, Math.min(rect.left, window.innerWidth - 290)), y });
              }}
              disabled={assignBusy === r.id}
              title={clientShown || t('bs_assign_client')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, maxWidth: 190, padding: '4px 8px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: '1px solid', borderColor: r.client_id ? 'var(--border)' : '#FECDCA', background: r.client_id ? 'var(--bg-app)' : '#FEF3F2', color: r.client_id ? 'var(--text-primary)' : '#B42318', cursor: 'pointer' }}>
              <i className={assignBusy === r.id ? 'ti ti-loader' : 'ti ti-user-search'} style={{ fontSize: 13, flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{clientShown || t('bs_assign_client')}</span>
            </button>
          </td>
        ) : (
        <td style={cell('left', WRAP)} title={clientShown} onClick={e => { if (r.match_state === 'matched' && r.match?.clientId && r.client_name) { e.stopPropagation(); window.open(`/m/client_dashboard?id=${r.match.clientId}`, '_blank'); } }}>
          <span style={r.match_state === 'matched' && r.match?.clientId && r.client_name ? { color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline', textDecorationColor: 'transparent' } : undefined}>{clientShown}</span>
        </td>
        )}
        <td style={cell('left', WRAP)} title={companyShown} onClick={e => { if (r.match_state === 'matched' && r.match?.clientId && r.counter_company) { e.stopPropagation(); window.open(`/m/client_dashboard?id=${r.match.clientId}`, '_blank'); } }}>
          <span style={r.match_state === 'matched' && r.match?.clientId && r.counter_company ? { color: 'var(--accent)', cursor: 'pointer' } : undefined}>{companyShown}</span>
        </td>
        {/* Quotation/Invoice — matched rows show the PARENT doc number, clickable → its detail, and not the receipt
            issued for it (Calem, report 39fdf13d: "Quotations and/or invoices only required in this column, not
            receipts") — its RE- is the Match column's. Every QO- / IV- / RE- number on this page is a DocNoLink
            (Calem, Bugify #b4cccf02): the Quotation / Invoice / Receipt blue, where it was a green, and a click opens
            its document over the page, where this column opened Peak in a new tab and the Match column's receipt
            number its PDF. A tick or icon beside a number keeps its colour and its click; a property-tax match still
            opens Tax. */}
        <td style={cell('left', { ...WRAP, color: 'var(--text-secondary)' })} title={r.match_state === 'matched' && r.match?.docNo ? r.match.docNo : r.quote_receipt} onClick={e => e.stopPropagation()}>
          {(r as any).allocations?.length > 1 ? (
            /* One payment, several documents: list every matched doc; the remainder (if any) shows amber. */
            <span>
              {(r as any).allocations.map((a: any) => (
                <span key={a.id} style={{ display: 'block' }}>
                  <span onClick={() => { if (a.doc_id && (a.doc_kind === 'quotation' || a.doc_kind === 'invoice')) window.open(`/m/peak?tab=${a.doc_kind === 'quotation' ? 'quotation' : 'invoice'}&open=${a.doc_id}`, '_blank'); }}
                    style={{ display: 'block', color: 'var(--accent)', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}><DocNoLink no={a.doc_no} id={a.doc_id} kind={a.doc_kind} /></span>
                </span>
              ))}
              {(r as any).remaining > 0.009 && <span style={{ display: 'block', color: '#B45309', fontSize: 11, fontWeight: 700 }}>฿{money((r as any).remaining)} left</span>}
            </span>
          ) : (r.match_state === 'matched' || r.match_state === 'partial') && r.match?.docNo ? (
            <span>
              <span onClick={() => {
                const m = r.match!;
                if (m.docKind === 'property_tax') window.open(`/m/tax?section=property_tax${m.ptYear ? `&year=${m.ptYear}` : ''}`, '_blank');
                else if (m.docId) window.open(`/m/peak?tab=${m.docKind === 'quotation' ? 'quotation' : 'invoice'}&open=${m.docId}`, '_blank');
              }} style={{ color: 'var(--accent)', fontWeight: 600, cursor: 'pointer' }}><DocNoLink no={r.match.docNo} id={r.match.docId} kind={r.match.docKind} /></span>
              {r.match_state === 'partial' && (r as any).remaining > 0.009 && <span style={{ display: 'block', color: '#B45309', fontSize: 11, fontWeight: 700 }}>฿{money((r as any).remaining)} left</span>}
            </span>
          ) : <DocNoLink no={r.quote_receipt} />}
        </td>
        {/* Payment Slip — click / drop / paste uploads; thumbnails open a lightbox. Deleted rows
            keep their slips visible (read-only history) but the cell disappears with the row. */}
        <td style={cell('left', { overflow: 'visible', padding: '4px 6px' })} onClick={e => e.stopPropagation()}>
          {!del0 && <SlipCell scope={scope} txnId={r.id} slips={r.slips || []} canConfirm={canSlipConfirm}
            onChanged={slips => setRows(rs => rs.map(x => x.id === r.id ? { ...x, slips } : x))} />}
        </td>
        {/* Comments shown in FULL — pre-wrap keeps line breaks (remark on its own line, then the
            original comments) and still wraps long lines instead of truncating. System-written
            lines (sys_lines) render green; the bank's Details line shows as just the client's name. */}
        <td style={cell('left', { color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word', overflow: 'visible' })}><CommentsCell row={r} /></td>
        <td style={cell('left', { overflow: 'visible' })} onClick={e => e.stopPropagation()}>{matchCell(r)}</td>
        <td style={cell('center')}><TypeBadge type={r.account_type} /></td>
        {showExtra && <td style={cell('left', { whiteSpace: 'normal', fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.45 })}>
          <div>{r.created_by_name || '—'}</div>
          <div>{fmtDT(r.created_at)}</div>
          {r.edited === 1 && <div style={{ color: '#B4894A' }}><i className="ti ti-pencil" style={{ fontSize: 10 }} /> {fmtDT(r.updated_at)}</div>}
          {del0 && <div style={{ color: '#E24B4A' }}><i className="ti ti-trash" style={{ fontSize: 10 }} /> {r.deleted_by_name} · {fmtDT(r.deleted_at)}</div>}
        </td>}
        <td style={cell('right', edgeR)} onClick={e => e.stopPropagation()}>
          {!del0 && <>
            <button onClick={() => { setEditing(r); setShowForm(true); }} title={t('edit')} style={{ border: 'none', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', marginRight: 4 }}><i className="ti ti-pencil" /></button>
            <button onClick={() => del(r)} title={t('delete')} style={{ border: 'none', background: 'none', color: '#E24B4A', cursor: 'pointer' }}><i className="ti ti-trash" /></button>
          </>}
        </td>
      </tr>
    );
  });

  // The page — round the list and between and under the cards — is white, --bg-surface, the column headings' own
  // (Calem, report ddf1ba0e, 3rd opening: "Change every page background to white"). The date bar and round the list it
  // was the app's beige, until Bugify #b07c8dae's 1st opening made the lot the Bank box's green one shade lighter,
  // #E9F8F2, and its 2nd the list's grey, --c-gray-50. The headings keep their thin line (report 66c9d174 — see
  // thBase), which parts them from the white under them.
  // The strips over the list — the toolbar, the date bar and the statement status bar — are the light grey,
  // --c-gray-50, as File Storage's toolbar band is and every top-menu page's now (Calem, report 3e769579, 4th opening:
  // "Only the top menu strip on every page turns that same grey then the rest of the page below this menu has a white
  // background"), each with its thin line under it. They were the page's white, the date bar since report ddf1ba0e.
  // Those lines are a shade darker, --border-strong where they were --border, as is the top menu's over the toolbar
  // (Calem, report a903bda5, 3rd opening: "Make the gray horizontal lines one shade darker on all page menus like this").
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--bg-surface)' }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderBottom: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', flexWrap: 'wrap' }}>
        {!badgeOnly && acctTab('all', t('bs_all'))}
        {!badgeOnly && scope !== 'client' && acctTab('corporate', t('cs_corporate'))}
        {!badgeOnly && scope !== 'client' && acctTab('personal', t('cs_personal'))}
        {!badgeOnly && scope === 'client' && (() => {
          const sel = clientOpts.tagged.find((c: any) => c.id === clientId);
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <select value={clientId} onChange={e => setClientId(e.target.value)}
                style={{ padding: '6px 10px', fontSize: 12.5, fontWeight: 600, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', maxWidth: 260 }}>
                <option value="">{t('bs_all_clients')}</option>
                {clientOpts.tagged.map((c: any) => (
                  <option key={c.id} value={c.id}>{displayCompanyName(c.name)} — ฿{money(c.balance)}</option>
                ))}
              </select>
              {sel && (
                <span title={t('bs_client_balance')} style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6, padding: '5px 12px', borderRadius: 999, background: '#E3EFE8', color: '#276749', fontSize: 12.5, fontWeight: 800, whiteSpace: 'nowrap' }}>
                  {t('bs_client_balance')}: ฿{money(sel.balance)}
                  <span style={{ fontWeight: 600, opacity: 0.75 }}>({t('bs_in')} ฿{money(sel.credit)} · {t('bs_out')} ฿{money(sel.debit)})</span>
                </span>
              )}
              {!clientId && clientOpts.untagged > 0 && (
                <span style={{ padding: '5px 10px', borderRadius: 999, background: '#FEF3F2', border: '1px solid #FECDCA', color: '#B42318', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {t('bs_unassigned', { n: clientOpts.untagged })}
                </span>
              )}
            </span>
          );
        })()}
        <div style={{ flex: 1 }} />
        {/* The search box is on the period row below, before Download PDF; here only while the badge strip hides that row. */}
        {badgeOnly && searchBox}
        <label style={{ ...btn('var(--c-gray-100)', 'var(--text-secondary)'), display: 'inline-flex', alignItems: 'center', opacity: importing ? 0.6 : 1 }}>
          <input type="file" accept="application/pdf,.pdf" style={{ display: 'none' }} disabled={importing} onChange={onImport} />
          <i className="ti ti-file-upload" style={{ fontSize: 14, marginRight: 6 }} aria-hidden />{importing ? t('login_wait') : t('bs_import')}
        </label>
        {matching && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--accent)', fontWeight: 600 }}><i className="ti ti-loader" style={{ fontSize: 14 }} aria-hidden />{t('bs_matching')}</span>}
        <button onClick={runAutomatch} disabled={matching} title={t('bs_automatch')} style={{ width: 32, height: 32, borderRadius: 'var(--radius-md)', border: 'none', background: 'transparent', color: matching ? 'var(--accent)' : 'var(--text-secondary)', cursor: matching ? 'default' : 'pointer' }}><i className={matching ? 'ti ti-loader' : 'ti ti-wand'} style={{ fontSize: 15 }} aria-hidden /></button>
        <button onClick={scanNow} disabled={scanning} title={t('bs_fetch')} style={{ width: 32, height: 32, borderRadius: 'var(--radius-md)', border: 'none', background: 'transparent', color: scanning ? 'var(--accent)' : 'var(--text-secondary)', cursor: scanning ? 'default' : 'pointer' }}><i className={scanning ? 'ti ti-loader' : 'ti ti-refresh'} style={{ fontSize: 15 }} aria-hidden /></button>
        {isMgmt && <button onClick={toggleExtraCols} title={extraCols ? t('bs_extra_cols_hide') : t('bs_extra_cols_show')} style={{ width: 32, height: 32, borderRadius: 'var(--radius-md)', border: 'none', background: 'transparent', color: extraCols ? 'var(--accent)' : 'var(--text-secondary)', cursor: 'pointer' }}><i className={`ti ti-chevrons-${extraCols ? 'left' : 'right'}`} style={{ fontSize: 15 }} aria-hidden /></button>}
      </div>

      {/* Badge drill-down strip replaces the date row — the badge count has no date window. */}
      {badgeOnly && <BadgeFilterBar count={total} labelKey="bf_bank" onClear={clearBadge} />}

      {/* Date filter + PDF download. Filters the list; the download exports exactly this range. */}
      {!badgeOnly && <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderBottom: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', flexWrap: 'wrap' }}>
        <i className="ti ti-calendar-event" style={{ fontSize: 15, color: 'var(--text-tertiary)' }} aria-hidden />
        {periodChip('today', t('bs_today'))}
        {periodChip('yesterday', t('bs_yesterday'))}
        {periodChip('week', t('bs_this_week'))}
        {periodChip('month', t('bs_this_month'))}
        {periodChip('lastMonth', t('bs_last_month'))}
        {periodChip('year', t('bs_this_year'))}
        {periodChip('custom', t('bs_custom'))}
        {period === 'custom' && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
            <input type="date" value={customFrom} max={customTo || undefined} onChange={e => setCustomFrom(e.target.value)} style={{ padding: '5px 8px', fontSize: 12, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)' }} />
            <span>–</span>
            <input type="date" value={customTo} min={customFrom || undefined} onChange={e => setCustomTo(e.target.value)} style={{ padding: '5px 8px', fontSize: 12, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)' }} />
          </span>
        )}
        <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{fmtDate(rangeFrom)} – {fmtDate(rangeTo)}</span>
        <div style={{ flex: 1 }} />
        {searchBox}
        {/* A bubble too (Calem, Bugify #cba01676, 5th opening: "Also change "Download PDF" to the rounded bubble"): round
            ends, the pills' 5px 12px and 12px, and a 1px line in its own green, as every pill has a line, so it stands as
            tall as they do. Its solid green, white words and icon are as they were. It was btn's 8px-cornered 8px 14px at
            13px, with no line. */}
        <button onClick={download} disabled={downloading || (period === 'custom' && (!customFrom || !customTo))}
          style={{ ...btn('var(--accent)'), padding: '5px 12px', fontSize: 12, borderRadius: 999, border: '1px solid var(--accent)', display: 'inline-flex', alignItems: 'center', opacity: (downloading || (period === 'custom' && (!customFrom || !customTo))) ? 0.6 : 1, cursor: downloading ? 'default' : 'pointer' }}>
          <i className={downloading ? 'ti ti-loader' : 'ti ti-download'} style={{ fontSize: 14, marginRight: 6, ...(downloading ? { display: 'inline-block', animation: 'spin 1s linear infinite' } : {}) }} aria-hidden />
          {downloading ? t('bs_downloading') : t('bs_download_pdf')}
        </button>
      </div>}

      {/* Statement status header — three time points:
          (1) system last scan (global), (2) last successful statement per account, (3) that statement's
          printed date range. Lets staff see the auto-fetch is alive and what the latest statement covers. */}
      {accounts.length > 0 && (
        <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border-strong)', background: 'var(--c-gray-50)' }}>
          {/* Click to expand/collapse the account + payment-QR cards — collapsed by default to keep the
              transactions table roomy on a laptop. A compact one-line summary stays visible when collapsed. */}
          <div onClick={toggleAcct} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 11.5, color: 'var(--text-secondary)' }}>
            <i className={`ti ti-chevron-${acctOpen ? 'down' : 'right'}`} style={{ fontSize: 13, color: 'var(--text-tertiary)' }} aria-hidden />
            <i className="ti ti-refresh-dot" style={{ fontSize: 13, color: 'var(--accent)' }} aria-hidden />
            {t('bs_last_scan')}: <b style={{ color: 'var(--text-primary)' }}>{fmtDT(lastScan) || '—'}</b>
            {(() => {
              // Owner 2026-08-25: the monthly balance line must ALSO live up here, always visible.
              // Pin the current month when the filter covers it, otherwise the newest month in range.
              const yms = Object.keys(monthly).sort().reverse();
              const ym = (viewYm && monthly[viewYm]) ? viewYm
                : yms.includes(todayISO().slice(0, 7)) ? todayISO().slice(0, 7) : yms[0];
              const m = ym ? monthly[ym] : null;
              return m ? (
                <span onClick={e => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center', gap: 13, marginLeft: 8, padding: '3px 12px', borderRadius: 999, background: 'var(--bg-app)', border: '1px solid var(--border)', fontWeight: 700, fontSize: 11.5, whiteSpace: 'nowrap', cursor: 'default' }}>
                  <b style={{ color: 'var(--text-primary)' }}>{monthLabel(ym)}</b>
                  <span style={{ color: 'var(--text-secondary)' }}>{t('bs_opening')}: ฿{money(m.opening)}</span>
                  <span style={{ color: '#E24B4A' }}>{t('bs_debit')}: ฿{money(m.debit)}</span>
                  <span style={{ color: '#2B6CB0' }}>{t('bs_credit')}: ฿{money(m.credit)}</span>
                  <b style={{ color: 'var(--text-primary)' }}>{t('bs_closing')}: ฿{money(m.end_balance)}</b>
                </span>
              ) : null;
            })()}
            {!acctOpen && <span style={{ color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>· {accounts.map((a: any) => `${t(acctTypeKey(a.account_type))}${a.account_number ? ' ' + a.account_number : ''}`).join('  ·  ')}</span>}
            <div style={{ flex: 1 }} />
            <span style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600 }}>{acctOpen ? t('bs_acct_hide') : t('bs_acct_show')}</span>
          </div>
          {acctOpen && <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
            {accounts.map((a: any) => (
              <div key={a.account_type + (a.account_number || '')} style={{ flex: '1 1 280px', minWidth: 250, border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', background: 'var(--bg-app)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--text-primary)' }}>{t(acctTypeKey(a.account_type))}</span>
                  {a.account_number && <span style={{ fontSize: 11.5, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{a.account_number}</span>}
                </div>
                {a.last_success ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11.5 }}>
                    <div style={{ color: 'var(--text-secondary)' }}><i className="ti ti-circle-check" style={{ fontSize: 12, marginRight: 4, color: '#15803D' }} aria-hidden />{t('bs_last_success')}: <b style={{ color: 'var(--text-primary)' }}>{fmtDT(a.last_success)}</b></div>
                    <div style={{ color: 'var(--text-secondary)' }}><i className="ti ti-calendar" style={{ fontSize: 12, marginRight: 4, color: 'var(--text-tertiary)' }} aria-hidden />{t('bs_statement_period')}: <b style={{ color: 'var(--text-primary)' }}>{a.period_from || a.period_to ? `${fmtDate(a.period_from)} – ${fmtDate(a.period_to)}` : '—'}</b></div>
                  </div>
                ) : (
                  <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('bs_no_statements')}</div>
                )}
                {/* Two payment QRs: the FULL card (to hand a payer directly) + the SIMPLIFIED one embedded in documents. */}
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)', display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                  {qrSlot(a, 'full', a.qr_code, t('bs_qr_full'))}
                  {qrSlot(a, 'invoice', a.qr_code_invoice, t('bs_qr_invoice'))}
                </div>
              </div>
            ))}
          </div>}
        </div>
      )}

      {/* The page's white, as on the Jobs lists: between the cards, down either side of them and on under the last
          one (see cardLine). */}
      <div ref={tableScrollRef} onScroll={onTableScroll} style={{ flex: 1, overflow: 'auto', background: 'var(--bg-surface)' }}>
        {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '10px 16px' }}>{err}</div>}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><i className="ti ti-loader" style={{ fontSize: 24, color: 'var(--text-tertiary)' }} aria-hidden /></div>
        ) : (
          // Separate borders with no spacing, as on the Jobs and Tracking lists: collapsed borders can't be rounded.
          // CARD_INSET of padding either side, where the page's white shows. Every box is border-box (global.css), so
          // the minimum width takes the padding on top of the columns' own, and no column comes in for it.
          <table style={{ borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed', width: '100%', minWidth: tableW + 2 * CARD_INSET, padding: `0 ${CARD_INSET}px` }}>
            <colgroup>{cols.map(c => <col key={c.key} style={{ width: c.w }} />)}</colgroup>
            {/* The first and last header cells let their thEdge show past them. */}
            <thead><tr>{cols.map((c, i) => <th key={c.key} style={{ ...thBase, textAlign: c.align, ...(i === 0 || i === cols.length - 1 ? { overflow: 'visible' } : {}) }}>
              {i === 0 && <span aria-hidden style={{ ...thEdge, left: -CARD_INSET }} />}
              {i === cols.length - 1 && <span aria-hidden style={{ ...thEdge, right: -CARD_INSET }} />}
              {c.key === '_actions' ? '' : c.key === '_sel' ? (
                <input type="checkbox" title={t('bs_pot_select_all')} style={{ cursor: 'pointer' }}
                  checked={rows.length > 0 && rows.every(r => sel.has(r.id))}
                  onChange={e => setSel(e.target.checked ? new Set(rows.map(r => r.id)) : new Set())} />
              ) : t(c.key)}</th>)}</tr></thead>
            <tbody>
              {bodyRows}
              {rows.length > 0 && cardGap('end')}
              {rows.length === 0 && <tr><td style={{ ...tdBase, textAlign: 'center', color: 'var(--text-tertiary)', padding: 40 }} colSpan={cols.length}>{t('bs_empty')}</td></tr>}
            </tbody>
          </table>
        )}
      </div>

      {sel.size > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', background: 'rgba(124,58,237,0.08)', borderTop: '1px solid rgba(124,58,237,0.3)' }}>
          {/* The string carries an {{amt}} too — the credit side of what is ticked. Passing only {n}
              printed the placeholder raw ("THB {{amt}} in") on the toolbar. */}
          <b style={{ fontSize: 12.5, color: '#6D28D9' }}>{t('bs_pot_selected', {
            n: sel.size,
            amt: money(rows.filter(r => sel.has(r.id)).reduce((s, r) => s + (Number(r.credit) || 0), 0)),
          })}</b>
          <button onClick={() => setPotModal(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 8, border: 'none', background: '#7C3AED', color: '#fff', fontWeight: 800, fontSize: 12.5, cursor: 'pointer' }}>
            <i className="ti ti-coins" aria-hidden />{t('bs_pot_group')}</button>
          <button onClick={() => setSel(new Set())} style={{ border: 'none', background: 'none', color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer' }}>{t('cancel')}</button>
        </div>
      )}
      {potModal && <TaxPotModal scope={scope} txns={rows.filter(r => sel.has(r.id))}
        onClose={() => setPotModal(false)} onSaved={() => { setSel(new Set()); setPotModal(false); load(); }} />}
      {potOpen && <TaxPotModal scope={scope} txns={[]} potId={potOpen}
        onClose={() => setPotOpen('')} onSaved={() => load()} />}
      <Pagination page={page} total={total} limit={pageSize} onPage={p => load(p)} onLimit={n => setPageSize(n)} />
      {preview && <TxnPreview
        scope={scope} txn={preview}
        onClose={() => setPreview(null)}
        onSaved={load}
        onEditFull={() => { setEditing(preview); setShowForm(true); setPreview(null); }}
        onMatch={() => { const p = preview; setPreview(null); openMatch(p); }}
        onOpenReceipt={() => { if (preview.receipt_id) openPdf(preview.receipt_id); }}
        onUnmatch={async () => { await undoMatch(preview); setPreview(null); }}
        onRematch={() => { const p = preview; setPreview(null); rematch(p); }}
      />}
      {emailFor && <ReceiptEmailComposer receiptId={emailFor.receiptId} defaultName={emailFor.name} onClose={() => setEmailFor(null)} onSent={load} />}
      {threadFor && <ReceiptEmailThread scope={scope} receiptId={threadFor.receiptId} docNo={threadFor.docNo} clientName={threadFor.name} onClose={() => setThreadFor(null)} />}
      {ptThreadFor && <PropertyTaxEmailThread clientId={ptThreadFor.clientId} year={ptThreadFor.year} company={ptThreadFor.name} onClose={() => setPtThreadFor(null)} />}
      {ptEmailFor && <PropertyTaxEmailComposer clientId={ptEmailFor.clientId} year={ptEmailFor.year} mode="payment" onClose={() => setPtEmailFor(null)} onSent={() => load()} />}
      <Fab onClick={() => { setEditing(null); setShowForm(true); }} title={t('bs_add')} color={tabColor({ accounting: 'bank_acc', legal: 'bank_legal', client: 'bank_client' }[scope])} />
      {taxEmailFor && <TaxPaidEmailComposer source="bank" txnId={taxEmailFor} onClose={() => setTaxEmailFor(null)} onSent={() => load()} />}
      {showForm && <TxnForm scope={scope} txn={editing} onClose={() => setShowForm(false)} onSaved={load} />}
      {selectedMatch && <MatchPanel
        payment={{ amount: (selectedMatch as any).remaining ?? selectedMatch.credit, dateLabel: fmtDate(selectedMatch.txn_date) + ((selectedMatch as any).txn_time ? ' ' + (selectedMatch as any).txn_time : ''), remitter: displayClientName(selectedMatch.client_name, selectedMatch.client_first, selectedMatch.client_last) || displayCompanyName(selectedMatch.counter_company) || '—', note: [selectedMatch.description, selectedMatch.comments].filter(Boolean).join(' · ') }}
        suggestion={selectedMatch.match || null}
        loadCandidates={(qq, amt, opts) => api.bankMatchCandidates(scope, qq, amt, opts).then((d: any) => d.candidates || [])}
        loadDocItems={(kind, id) => api.bankDocItems(scope, kind, id)}
        confirmMatch={(body) => api.bankConfirmMatch(scope, selectedMatch.id, body)}
        ignoreMatch={() => api.bankIgnoreMatch(scope, selectedMatch.id)}
        kindFilter={matchKind}
        txnSource="bank" txnId={selectedMatch.id}
        onClose={() => { setSelectedMatch(null); setMatchKind(undefined); }} onDone={() => load()} />}

      {/* Match / No-match dropdown menu (fixed-positioned, so it never clips inside the scroll area). */}
      {/* Assign Client — type-to-search; deliberately shows NO list until a query is typed. */}
      {clientPick && (() => {
        const row = rowById(clientPick.id);
        const q = clientQuery.trim().toLowerCase();
        const hits = q ? clientOpts.all.filter((c: any) => String(c.name || '').toLowerCase().includes(q)).slice(0, 25) : [];
        const pick = async (cid: string) => { const id = clientPick.id; setClientPick(null); await assignClient(id, cid); };
        return (
          <>
            <div onClick={() => setClientPick(null)} style={{ position: 'fixed', inset: 0, zIndex: 11000 }} />
            <div style={{ position: 'fixed', left: clientPick.x, top: clientPick.y, zIndex: 11001, width: 280, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
              <div style={{ padding: 8, borderBottom: '1px solid var(--border)' }}>
                <input autoFocus value={clientQuery} onChange={e => setClientQuery(e.target.value)} placeholder={t('bs_client_search_ph')}
                  style={{ width: '100%', padding: '7px 10px', fontSize: 13, borderRadius: 7, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ maxHeight: 230, overflowY: 'auto' }}>
                {!q && <div style={{ padding: '14px 13px', fontSize: 12.5, color: 'var(--text-tertiary)' }}>{t('bs_client_search_hint')}</div>}
                {q && hits.length === 0 && <div style={{ padding: '14px 13px', fontSize: 12.5, color: 'var(--text-tertiary)' }}>{t('bs_client_no_match')}</div>}
                {hits.map((c: any) => (
                  <button key={c.id} onClick={() => pick(c.id)}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 13px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-active)')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                    {displayCompanyName(c.name)}{c.email ? <span style={{ display: 'block', fontSize: 11, fontWeight: 500, color: 'var(--text-tertiary)' }}>{c.email}</span> : null}
                  </button>
                ))}
              </div>
              {row && row.client_id && (
                <button onClick={() => pick('')} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 13px', borderTop: '1px solid var(--border)', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: '#B42318' }}>
                  {t('bs_client_clear')}
                </button>
              )}
            </div>
          </>
        );
      })()}

      {menuFor && (() => {
        const r = rowById(menuFor.id);
        if (!r) return null;
        const item = (icon: string, label: string, onClick: () => void, danger = false) => (
          <button onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', textAlign: 'left', padding: '9px 13px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: danger ? '#B42318' : 'var(--text-primary)' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-active)')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
            <i className={icon} style={{ fontSize: 15, width: 16, textAlign: 'center' }} />{label}
          </button>
        );
        return (
          <>
            <div onClick={() => setMenuFor(null)} style={{ position: 'fixed', inset: 0, zIndex: 11000 }} />
            <div style={{ position: 'fixed', left: menuFor.x, top: menuFor.y, zIndex: 11001, minWidth: 234, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: 'var(--shadow-md)', overflow: 'hidden', padding: '4px 0' }}>
              {item('ti ti-plug-connected', t('bs_nm_match'), () => { setMenuFor(null); setMatchKind(undefined); openMatch(r); })}
              {item('ti ti-receipt-tax', t('bs_match_vat'), () => { setMenuFor(null); setMatchKind('vat'); setSelectedMatch(r); })}
              {item('ti ti-calendar-stats', t('bs_match_pnd51'), () => { setMenuFor(null); setMatchKind('pnd51'); setSelectedMatch(r); })}
              {item('ti ti-percentage', t('bs_match_wht'), () => { setMenuFor(null); setMatchKind('wht'); setSelectedMatch(r); })}
              <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />
              {NM_FIXED.map(o => item(o.icon, o.label, () => {
                setMenuFor(null);
                if (o.ask === 'notary') { setNotaryFirst(''); setNotaryLast(''); setNotaryFor(r); return; }
                doNoMatch(r, o.reason);
              }, true))}
              {item('ti ti-pencil', t('bs_nm_custom'), () => { setMenuFor(null); setCustomReason(''); setCustomFor(r); }, true)}
            </div>
          </>
        );
      })()}

      {/* Custom no-match reason → written to Comments. */}
      {notaryFor && (() => {
        // Last name is required; a first name alone gives "Notary Public : Ursula", which is exactly the
        // ambiguity this replaces. The saved reason is the chip text and the green Comments line.
        const full = `${notaryLast.trim()}${notaryLast.trim() && notaryFirst.trim() ? ', ' : ''}${notaryFirst.trim()}`.trim();
        const ok = !!notaryLast.trim();
        const save = () => { const rr = notaryFor; setNotaryFor(null); doNoMatch(rr, `Notary Public : ${full}`); };
        const fld: React.CSSProperties = { width: '100%', padding: '9px 12px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' };
        return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 11002, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => e.target === e.currentTarget && setNotaryFor(null)}>
          <div style={{ width: 420, maxWidth: '94vw', background: 'var(--bg-surface)', borderRadius: 14, boxShadow: 'var(--shadow-md)', padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}><i className="ti ti-stamp" style={{ marginRight: 6 }} />{t('bs_notary_title')}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t('bs_notary_hint')}</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{t('bs_notary_first')}</label>
                <input value={notaryFirst} autoFocus onChange={e => setNotaryFirst(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && ok) save(); }} style={fld} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{t('bs_notary_last')} *</label>
                <input value={notaryLast} onChange={e => setNotaryLast(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && ok) save(); }} style={fld} />
              </div>
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
              {t('bs_notary_preview')}: <b style={{ color: 'var(--text-primary)' }}>Notary Public{full ? ` : ${full}` : ''}</b>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setNotaryFor(null)} style={btn('var(--c-gray-100)', 'var(--text-secondary)')}>{t('cancel')}</button>
              <button onClick={save} disabled={!ok} style={{ ...btn('var(--accent)'), opacity: ok ? 1 : 0.6 }}>{t('save')}</button>
            </div>
          </div>
        </div>
        );
      })()}

      {customFor && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 11002, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => e.target === e.currentTarget && setCustomFor(null)}>
          <div style={{ width: 400, maxWidth: '94vw', background: 'var(--bg-surface)', borderRadius: 14, boxShadow: 'var(--shadow-md)', padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}>{t('bs_nm_custom_title')}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t('bs_nm_custom_hint')}</div>
            <textarea value={customReason} onChange={e => setCustomReason(e.target.value)} rows={3} placeholder={t('bs_nm_custom_ph')} autoFocus
              style={{ width: '100%', padding: '9px 12px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box', resize: 'vertical' }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setCustomFor(null)} style={btn('var(--c-gray-100)', 'var(--text-secondary)')}>{t('cancel')}</button>
              <button onClick={() => { const rr = customFor; const txt = customReason.trim(); if (!txt) return; setCustomFor(null); doNoMatch(rr, txt); }} disabled={!customReason.trim()} style={{ ...btn('var(--accent)'), opacity: customReason.trim() ? 1 : 0.6 }}>{t('save')}</button>
            </div>
          </div>
        </div>
      )}

      {qrZoom && (
        <div onClick={() => setQrZoom(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 11000, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 16, maxWidth: 'min(94vw, 460px)', display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center', boxShadow: '0 12px 50px #0006' }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)' }}>{t(acctTypeKey(qrZoom.acc.account_type))}{qrZoom.acc.account_number ? ` · ${qrZoom.acc.account_number}` : ''} <span style={{ color: 'var(--text-tertiary)', fontWeight: 600 }}>· {qrZoom.slot === 'invoice' ? t('bs_qr_invoice') : t('bs_qr_full')}</span></div>
            <img src={qrZoom.src} alt="QR" style={{ maxWidth: '100%', maxHeight: '68vh', objectFit: 'contain', background: '#fff', borderRadius: 8 }} />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
              <a href={qrZoom.src} download={`payment-qr-${qrZoom.acc.account_type}-${qrZoom.slot}.png`} style={{ ...btn('#1D7C4D'), textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}><i className="ti ti-download" />{t('bs_qr_download')}</a>
              <label style={{ ...btn('var(--c-gray-100)', 'var(--text-secondary)'), display: 'inline-flex', alignItems: 'center', gap: 6, cursor: qrBusy ? 'default' : 'pointer' }}>
                <i className="ti ti-replace" />{qrBusy ? t('bs_qr_uploading') : t('bs_qr_replace')}
                <input type="file" accept="image/*" style={{ display: 'none' }} disabled={!!qrBusy} onChange={e => { const f = e.target.files?.[0]; if (f) uploadQr(qrZoom.acc, f, qrZoom.slot); e.currentTarget.value = ''; }} />
              </label>
              <button onClick={() => removeQr(qrZoom.acc, qrZoom.slot)} disabled={!!qrBusy} style={btn('transparent', '#E24B4A')}><i className="ti ti-trash" style={{ marginRight: 5 }} />{t('bs_qr_remove')}</button>
              <button onClick={() => setQrZoom(null)} style={btn('var(--c-gray-100)', 'var(--text-secondary)')}>{t('close')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Standalone module page ──────────────────────────────────────────────────
// ── 代缴税款池 · Tax pot (owner 2026-09-08) ─────────────────────────────────────────────────────
// A client pays the tax we remit in whatever instalments suit them; we send the authority one
// payment. This ties those lines to ONE obligation without touching the statement: the bank said
// five deposits, and the balance column is built line by line, so nothing here merges rows.
//
// The expected amount is never typed — the half-year worksheet for PND.51, the Filing Center for
// everything else (owner's ruling). When the money and the tax differ the operator picks a reason
// and decides whether that settles it; there is no silent write-off.
const r2 = (n: number) => Math.round(n * 100) / 100;
const POT_TAXES: { key: string; label: string; monthly: boolean }[] = [
  { key: 'pnd51', label: 'PND.51 (half-year)', monthly: false },
  { key: 'pnd50', label: 'PND.50 (corporate annual)', monthly: false },
  { key: 'pnd94', label: 'PND.94 (personal half-year)', monthly: false },
  { key: 'pnd9091', label: 'PND.90/91 (personal annual)', monthly: false },
  { key: 'vat', label: 'VAT (PP.30)', monthly: true },
  { key: 'pnd1', label: 'PND.1', monthly: true },
  { key: 'pnd3', label: 'PND.3', monthly: true },
  { key: 'pnd53', label: 'PND.53', monthly: true },
  { key: 'sso', label: 'Social Security (SSO)', monthly: true },
];
const GAP_REASON_OPTS: { key: string; label: string }[] = [
  { key: 'bank_fee', label: 'Bank / transfer fee' },
  { key: 'rounding', label: 'Rounding on the payment' },
  { key: 'client_short', label: 'Client paid short' },
  { key: 'client_over', label: 'Client paid over' },
  { key: 'partial', label: 'Part payment — more to come' },
  { key: 'other', label: 'Other (explain below)' },
];

function TaxPotModal({ scope, txns, potId, onClose, onSaved }: {
  scope: string; txns: BankTxn[]; potId?: string; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [clients, setClients] = useState<any[]>([]);
  const [clientId, setClientId] = useState('');
  const [taxKey, setTaxKey] = useState('pnd51');
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [expected, setExpected] = useState<any>(null);
  const [pot, setPot] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [remitOn, setRemitOn] = useState('');           // the date box; '' = fall back to the suggestion
  const monthly = POT_TAXES.find(x => x.key === taxKey)?.monthly !== false;

  useEffect(() => { if (potId) api.bankPot(scope, potId).then(setPot).catch((e: any) => setErr(e.message)); }, [potId]); // eslint-disable-line
  // The whole list once, filtered in the picker — the same component and the same call the
  // Invoice / Receipt forms use. It replaced a search box feeding a separate collapsed <select>:
  // typing filtered the hidden option list, so the screen showed nothing happening and the feature
  // read as broken (owner 2026-09-16).
  useEffect(() => { api.getClients({ all: true }).then((d: any) => setClients(d.rows || d || [])).catch(() => {}); }, []);
  // The books, not the operator, say what is owed — re-read whenever the target changes.
  useEffect(() => {
    if (!clientId) { setExpected(null); return; }
    api.bankPotExpected(scope, clientId, taxKey, year, monthly ? month : 0).then(setExpected).catch(() => setExpected(null));
  }, [clientId, taxKey, year, month, monthly]); // eslint-disable-line

  const selectedFunding = txns.filter(x => Number(x.credit) > 0);
  const selectedRemit = txns.filter(x => Number(x.debit) > 0);
  const fundedNew = r2(selectedFunding.reduce((s, x) => s + Number(x.credit || 0), 0));
  const view = pot || null;
  const funded = r2((view?.funded || 0) + (potId ? 0 : fundedNew));
  const exp = r2(view ? view.expected : Number(expected?.amount) || 0);
  const gap = r2(funded - exp);

  const save = async () => {
    setBusy(true); setErr('');
    try {
      let id = potId;
      if (!id) {
        if (!clientId) throw new Error(t('bs_pot_pick_client'));
        const created: any = await api.bankPotCreate(scope, { clientId, taxKey, year, month: monthly ? month : 0 });
        id = created.id;
      }
      const items = [
        ...selectedFunding.map(x => ({ txnId: x.id, src: 'bank', role: 'funding', amount: Number(x.credit) })),
        ...selectedRemit.map(x => ({ txnId: x.id, src: 'bank', role: 'remittance', amount: Number(x.debit) })),
      ];
      if (items.length) await api.bankPotAttach(scope, id!, items);
      const fresh: any = await api.bankPot(scope, id!);
      setPot(fresh);
      onSaved();
    } catch (e: any) { setErr(e?.message || 'Failed'); }
    finally { setBusy(false); }
  };
  const settle = async (settled: boolean) => {
    if (!pot) return;
    setBusy(true); setErr('');
    try { setPot(await api.bankPotSettle(scope, pot.id, { settled, reason, note })); onSaved(); }
    catch (e: any) { setErr(e?.message || 'Failed'); }
    finally { setBusy(false); }
  };

  // Undo runs in the reverse order the pot was built: un-tick Paid → Re-open → detach / dissolve.
  // `frozen` is that gate in one place, so the ×, the dissolve button and the server agree.
  const remitLeg = (view?.lines || []).find((l: any) => l.role === 'remittance');
  const hasRemitLeg = !!remitLeg;
  const suggestedRemitOn = view?.remittedOn || remitLeg?.txnDate || new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 10);
  const frozen = !!(view && (view.remittedOn || view.status === 'settled'));

  const setRemitted = async (on: string) => {
    if (!pot) return;
    setBusy(true); setErr('');
    try { const fresh = await api.bankPotRemitted(scope, pot.id, on); setPot(fresh); setRemitOn(on); onSaved(); }
    catch (e: any) { setErr(e?.message || 'Failed'); }
    finally { setBusy(false); }
  };

  const dissolve = async () => {
    if (!pot) return;
    if (!window.confirm(t('bs_pot_dissolve_confirm', { n: (view?.lines || []).length }))) return;
    setBusy(true); setErr('');
    try { await api.bankPotDissolve(scope, pot.id); onSaved(); onClose(); }
    catch (e: any) { setErr(e?.message || 'Failed'); setBusy(false); }
  };

  const box: React.CSSProperties = { padding: '9px 11px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 9, background: 'var(--c-gray-50)', outline: 'none', boxSizing: 'border-box' };
  const lbl2: React.CSSProperties = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--text-secondary)', display: 'block', marginBottom: 4 };
  const money2 = (n: any) => (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 10120, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '5vh 16px' }}
      onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: 720, maxWidth: '96vw', background: 'var(--bg-surface)', borderRadius: 14, padding: 22, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ fontSize: 16, fontWeight: 800 }}>{t('bs_pot_title')}</div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 3 }}>{t('bs_pot_hint')}</div>

        {!potId && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
            <div style={{ flex: '2 1 240px' }}>
              <label style={lbl2}>{t('bs_pot_client')}</label>
              <ClientPicker clients={clients as ClientOpt[]} value={clientId} onPick={setClientId}
                firm={scope === 'legal' ? 'legal' : 'accounting'}
                onCreated={(c: ClientOpt) => setClients(prev => [...prev, c])} />
            </div>
            <div style={{ flex: '1 1 170px' }}><label style={lbl2}>{t('bs_pot_tax')}</label>
              <select value={taxKey} onChange={e => setTaxKey(e.target.value)} style={{ ...box, width: '100%' }}>
                {POT_TAXES.map(x => <option key={x.key} value={x.key}>{x.label}</option>)}
              </select></div>
            <div style={{ flex: '0 0 96px' }}><label style={lbl2}>{t('bs_pot_year')}</label>
              <input type="number" value={year} onChange={e => setYear(Number(e.target.value) || year)} style={{ ...box, width: '100%' }} /></div>
            {monthly && <div style={{ flex: '0 0 88px' }}><label style={lbl2}>{t('bs_pot_month')}</label>
              <input type="number" min={1} max={12} value={month} onChange={e => setMonth(Number(e.target.value) || month)} style={{ ...box, width: '100%' }} /></div>}
          </div>
        )}

        {/* The three numbers that matter, and the difference between them. */}
        <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
          {[[t('bs_pot_expected'), exp, expected?.detail || (view ? view.expectedSource : '')],
            [t('bs_pot_funded'), funded, `${selectedFunding.length + (view?.lines?.filter((l: any) => l.role === 'funding').length || 0)} ${t('bs_pot_lines')}`],
            [t('bs_pot_remitted'), view?.remitted ?? r2(selectedRemit.reduce((s, x) => s + Number(x.debit || 0), 0)), '']].map(([label, val, sub]: any, i) => (
            <div key={i} style={{ flex: '1 1 150px', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', background: 'var(--bg-app)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{label}</div>
              <div style={{ fontSize: 16, fontWeight: 800, marginTop: 2 }}>฿{money2(val)}</div>
              {sub && <div style={{ fontSize: 10.5, color: 'var(--text-tertiary)', marginTop: 2 }}>{sub}</div>}
            </div>
          ))}
        </div>
        {exp > 0 && Math.abs(gap) > 0.005 && (
          <div style={{ marginTop: 10, fontSize: 12.5, color: '#92400E', background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 9, padding: '9px 11px' }}>
            <i className="ti ti-alert-triangle" style={{ marginRight: 6 }} />
            {t(gap > 0 ? 'bs_pot_gap_over' : 'bs_pot_gap_short', { amt: money2(Math.abs(gap)) })}
          </div>
        )}
        {exp === 0 && clientId && expected?.source === 'none' && (
          <div style={{ marginTop: 10, fontSize: 12.5, color: '#B42318' }}>{expected.detail}</div>
        )}

        {view && (
          <div style={{ marginTop: 14, border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
            {view.lines.map((l: any) => (
              <div key={l.txnId} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '7px 11px', borderBottom: '1px solid var(--border)', fontSize: 12.5 }}>
                <span style={{ color: 'var(--text-tertiary)', width: 82 }}>{l.txnDate}</span>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.description}</span>
                <span style={{ fontSize: 10.5, fontWeight: 800, padding: '1px 7px', borderRadius: 999, background: l.role === 'remittance' ? 'rgba(219,39,119,0.10)' : 'rgba(29,158,117,0.12)', color: l.role === 'remittance' ? '#DB2777' : '#0F766E' }}>
                  {l.role === 'remittance' ? t('bs_pot_role_out') : t('bs_pot_role_in')}
                </span>
                <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>฿{money2(l.amount)}</span>
                {frozen
                  ? <span title={view.remittedOn ? t('bs_pot_locked_paid') : t('bs_pot_locked_settled')} style={{ color: 'var(--text-tertiary)', fontWeight: 800, cursor: 'not-allowed' }}><i className="ti ti-lock" style={{ fontSize: 13 }} /></span>
                  : <span onClick={async () => {
                      if (!window.confirm(t('bs_pot_detach_confirm', { date: l.txnDate, amt: money2(l.amount) }))) return;
                      try { setPot(await api.bankPotDetach(scope, view.id, l.txnId)); onSaved(); } catch (e: any) { setErr(e.message); } }}
                    style={{ cursor: 'pointer', color: '#E24B4A', fontWeight: 800 }} title={t('bs_pot_detach')}>×</span>}
              </div>
            ))}
          </div>
        )}

        {view && (
          <div style={{ marginTop: 14 }}>
            <label style={lbl2}>{t('bs_pot_gap_reason')}</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <select value={reason || view.gapReason} onChange={e => setReason(e.target.value)} style={{ ...box, flex: '1 1 220px' }}>
                <option value="">{t('bs_pot_gap_pick')}</option>
                {GAP_REASON_OPTS.map(x => <option key={x.key} value={x.key}>{x.label}</option>)}
              </select>
              <input value={note} onChange={e => setNote(e.target.value)} placeholder={t('bs_pot_note_ph')} style={{ ...box, flex: '2 1 240px' }} />
            </div>
          </div>
        )}

        {/* Paid to the Revenue Department (owner 2026-09-10) — the same tick VAT/SSO already have,
            but at the grain a tax is actually remitted: one client, one tax, one period. The date
            defaults to the outgoing payment's own bank date (NOT today) because the tick is
            usually placed after the fact; a pot with no outgoing leg — cash, or another company's
            account — is still tickable with the date typed in. */}
        {view && (
          <div style={{ marginTop: 14, border: '1px solid', borderColor: view.remittedOn ? '#A7F3D0' : 'var(--border)', borderRadius: 10, padding: '11px 13px', background: view.remittedOn ? '#ECFDF5' : 'var(--bg-app)' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: busy ? 'default' : 'pointer', fontSize: 13, fontWeight: 700 }}>
              <input type="checkbox" checked={!!view.remittedOn} disabled={busy}
                onChange={e => setRemitted(e.target.checked ? (remitOn || suggestedRemitOn) : '')} style={{ cursor: 'pointer' }} />
              {t('bs_pot_remitted_tick')}
            </label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
              <input type="date" value={remitOn || suggestedRemitOn} disabled={busy}
                onChange={e => { setRemitOn(e.target.value); if (view.remittedOn) setRemitted(e.target.value); }}
                style={{ ...box, width: 172, padding: '7px 9px' }} />
              {view.remittedOn
                ? <span style={{ fontSize: 12, color: '#065F46', fontWeight: 700 }}>{t('bs_pot_remitted_by', { name: view.remittedByName || '—' })}</span>
                : <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{hasRemitLeg ? t('bs_pot_remitted_hint') : t('bs_pot_remitted_hint_noleg')}</span>}
            </div>
          </div>
        )}

        {err && <div style={{ fontSize: 12.5, color: '#E24B4A', marginTop: 12 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 18, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {view && !frozen && (
            <button onClick={dissolve} disabled={busy} title={t('bs_pot_dissolve_hint')}
              style={{ marginRight: 'auto', padding: '9px 16px', borderRadius: 10, border: '1.5px solid #FECDCA', background: 'transparent', color: '#B42318', fontWeight: 800, fontSize: 13, cursor: busy ? 'default' : 'pointer' }}>
              <i className="ti ti-unlink" style={{ fontSize: 14, marginRight: 5 }} />{t('bs_pot_dissolve')}
            </button>
          )}
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 10, border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{t('cancel')}</button>
          {view && view.status === 'settled'
            ? <button onClick={() => settle(false)} disabled={busy} style={{ padding: '9px 18px', borderRadius: 10, border: '1.5px solid #B45309', background: 'transparent', color: '#B45309', fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>{t('bs_pot_reopen')}</button>
            : view && <button onClick={() => settle(true)} disabled={busy} style={{ padding: '9px 18px', borderRadius: 10, border: 'none', background: '#0E7C66', color: '#fff', fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>{t('bs_pot_settle')}</button>}
          <button onClick={save} disabled={busy} style={{ padding: '9px 22px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 800, fontSize: 13.5, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}>
            {busy ? t('login_wait') : potId ? t('bs_pot_add_lines') : t('bs_pot_create')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BankStatement({ scope, moduleKey }: { scope: 'accounting' | 'legal'; moduleKey: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        <button onClick={() => navigate('/')} title="SLF" style={{ width: 30, height: 30, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img className="app-logo" src="/logo.png" alt="SLF" style={{ width: 28, height: 28, objectFit: 'contain' }} />
        </button>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{t(`mod_${moduleKey}`)}</div>
      </header>
      <BankStatementView scope={scope} />
    </div>
  );
}
